// 🏆 TORNEI V3 - Sistema Tornei Avanzato
// Frontend con Wizard step-by-step

import React, { useState, useEffect, useCallback } from 'react';
import { BracketTree, MultiBracketTabs, ImportedTournamentBracket } from './components/BracketTree';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// ============================================
// 🎨 COSTANTI
// ============================================

const TOURNAMENT_TYPES = [
  { 
    value: 'campionato', 
    label: 'Campionato', 
    icon: '🏆',
    desc: 'Solo gironi con classifica finale',
    color: 'from-blue-500 to-blue-600'
  },
  { 
    value: 'torneo', 
    label: 'Torneo', 
    icon: '⚽',
    desc: 'Gironi + fase eliminazione',
    color: 'from-green-500 to-green-600'
  },
  { 
    value: 'random_magic', 
    label: 'Random Magic', 
    icon: '🎲',
    desc: 'Giornate estratte a sorte',
    color: 'from-purple-500 to-purple-600',
    disabled: true // Fase 3
  }
];

const QUALIFICATION_METHODS = [
  {
    value: 'direct',
    label: 'Qualificazione Diretta',
    icon: '🎯',
    desc: 'Le prime N posizioni di ogni girone passano'
  },
  {
    value: 'transversal',
    label: 'Ranking Trasversale',
    icon: '📊',
    desc: 'Primi + migliori secondi/terzi tra tutti i gironi'
  },
  {
    value: 'fasce',
    label: 'Sistema Fasce',
    icon: '🥇',
    desc: 'Divisione in Oro, Argento, Bronzo con bracket separati'
  },
  {
    value: 'placement',
    label: 'Finali per Piazzamento',
    icon: '⚡',
    desc: 'Torneo Fast: 1° vs 1°, 2° vs 2°, 3° vs 3°...',
    requiresGroups: 2
  }
];

const KNOCKOUT_FORMATS = [
  { value: 'single', label: 'Partita Secca', icon: '⚡' },
  { value: 'home_away', label: 'Andata/Ritorno', icon: '🔄' },
  { value: 'double', label: 'Doppia Eliminazione', icon: '♻️', disabled: true }
];

// Turni eliminazione disponibili per Andata/Ritorno
const KNOCKOUT_ROUNDS = [
  { value: 'sedicesimi', label: 'Sedicesimi', minTeams: 32 },
  { value: 'ottavi', label: 'Ottavi di Finale', minTeams: 16 },
  { value: 'quarti', label: 'Quarti di Finale', minTeams: 8 },
  { value: 'semifinale', label: 'Semifinale', minTeams: 4 },
  { value: 'finale', label: 'Finale', minTeams: 2 }
];

const WIZARD_STEPS = [
  { id: 1, name: 'Tipo', icon: '🎯' },
  { id: 2, name: 'Gironi', icon: '📋' },
  { id: 3, name: 'Qualificazione', icon: '🏅' },
  { id: 4, name: 'Eliminazione', icon: '🏆' },
  { id: 5, name: 'Partecipanti', icon: '👥' },
  { id: 6, name: 'Assegna', icon: '✋' },
  { id: 7, name: 'Riepilogo', icon: '✅' }
];

// Colori predefiniti per le fasce
const FASCE_COLORS = [
  { value: 'yellow', label: 'Oro', bgClass: 'bg-yellow-100', textClass: 'text-yellow-800', borderClass: 'border-yellow-400' },
  { value: 'gray', label: 'Argento', bgClass: 'bg-gray-200', textClass: 'text-gray-700', borderClass: 'border-gray-400' },
  { value: 'amber', label: 'Bronzo', bgClass: 'bg-amber-100', textClass: 'text-amber-800', borderClass: 'border-amber-500' },
  { value: 'blue', label: 'Blu', bgClass: 'bg-blue-100', textClass: 'text-blue-800', borderClass: 'border-blue-400' },
  { value: 'green', label: 'Verde', bgClass: 'bg-green-100', textClass: 'text-green-800', borderClass: 'border-green-400' },
  { value: 'purple', label: 'Viola', bgClass: 'bg-purple-100', textClass: 'text-purple-800', borderClass: 'border-purple-400' }
];

// Helper per ottenere le classi del colore
const getFasciaColorClasses = (color) => {
  return FASCE_COLORS.find(c => c.value === color) || FASCE_COLORS[0];
};

// ============================================
// 🥇 FASCE CONFIG SECTION COMPONENT
// ============================================

const FasceConfigSection = ({ formData, setFormData }) => {
  const numGroups = formData.group_stage.number_of_groups;
  const participantsPerGroup = formData.participants.length > 0 
    ? Math.ceil(formData.participants.length / numGroups)
    : 4;
  
  // Genera le posizioni disponibili in base ai partecipanti per girone
  const availablePositions = Array.from({ length: participantsPerGroup }, (_, i) => i + 1);
  
  // Aggiungi una nuova fascia
  const addFascia = () => {
    const currentFasce = formData.fasce.config || [];
    const usedPositions = currentFasce.flatMap(f => f.positions);
    const nextPosition = availablePositions.find(p => !usedPositions.includes(p)) || currentFasce.length + 1;
    const nextColor = FASCE_COLORS[currentFasce.length % FASCE_COLORS.length];
    
    const newFascia = {
      name: `Fascia ${currentFasce.length + 1}`,
      positions: [nextPosition],
      color: nextColor.value
    };
    
    setFormData(prev => ({
      ...prev,
      fasce: {
        ...prev.fasce,
        enabled: true,
        config: [...(prev.fasce.config || []), newFascia]
      }
    }));
  };
  
  // Rimuovi una fascia
  const removeFascia = (index) => {
    setFormData(prev => ({
      ...prev,
      fasce: {
        ...prev.fasce,
        config: prev.fasce.config.filter((_, i) => i !== index)
      }
    }));
  };
  
  // Aggiorna nome fascia
  const updateFasciaName = (index, name) => {
    setFormData(prev => ({
      ...prev,
      fasce: {
        ...prev.fasce,
        config: prev.fasce.config.map((f, i) => i === index ? { ...f, name } : f)
      }
    }));
  };
  
  // Aggiorna colore fascia
  const updateFasciaColor = (index, color) => {
    setFormData(prev => ({
      ...prev,
      fasce: {
        ...prev.fasce,
        config: prev.fasce.config.map((f, i) => i === index ? { ...f, color } : f)
      }
    }));
  };
  
  // Toggle posizione per fascia
  const togglePosition = (fasciaIndex, position) => {
    setFormData(prev => {
      const newConfig = [...prev.fasce.config];
      const fascia = { ...newConfig[fasciaIndex] };
      
      if (fascia.positions.includes(position)) {
        // Rimuovi posizione (solo se non è l'ultima)
        if (fascia.positions.length > 1) {
          fascia.positions = fascia.positions.filter(p => p !== position);
        }
      } else {
        // Aggiungi posizione (rimuovendola da altre fasce)
        newConfig.forEach((f, i) => {
          if (i !== fasciaIndex && f.positions.includes(position)) {
            newConfig[i] = { ...f, positions: f.positions.filter(p => p !== position) };
          }
        });
        fascia.positions = [...fascia.positions, position].sort((a, b) => a - b);
      }
      
      newConfig[fasciaIndex] = fascia;
      
      return {
        ...prev,
        fasce: { ...prev.fasce, config: newConfig }
      };
    });
  };
  
  // Calcola quanti giocatori per fascia
  const getPlayersPerFascia = (fascia) => {
    return fascia.positions.length * numGroups;
  };
  
  const fasce = formData.fasce.config || [];
  const usedPositions = fasce.flatMap(f => f.positions);
  const unusedPositions = availablePositions.filter(p => !usedPositions.includes(p));

  return (
    <div className="space-y-6 mt-6">
      {/* Intro */}
      <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-6 rounded-xl border border-yellow-200">
        <h4 className="font-bold text-yellow-800 mb-2">🥇 Sistema Fasce Multiple</h4>
        <p className="text-sm text-yellow-700">
          Dividi i giocatori in fasce diverse (es. Oro, Argento, Bronzo) in base al piazzamento nei gironi.
          Ogni fascia avrà il suo bracket eliminatorio separato con il proprio vincitore.
        </p>
      </div>
      
      {/* Lista fasce configurate */}
      <div className="space-y-4">
        {fasce.map((fascia, index) => {
          const colorClasses = getFasciaColorClasses(fascia.color);
          
          return (
            <div 
              key={index}
              className={`p-5 rounded-xl border-2 ${colorClasses.borderClass} ${colorClasses.bgClass}`}
              data-testid={`fascia-config-${index}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">
                    {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : '🏅'}
                  </span>
                  <input
                    type="text"
                    value={fascia.name}
                    onChange={(e) => updateFasciaName(index, e.target.value)}
                    className={`font-bold text-lg bg-transparent border-b-2 ${colorClasses.borderClass} ${colorClasses.textClass} focus:outline-none`}
                    data-testid={`fascia-name-${index}`}
                  />
                </div>
                
                <div className="flex items-center gap-2">
                  {/* Selettore colore */}
                  <select
                    value={fascia.color}
                    onChange={(e) => updateFasciaColor(index, e.target.value)}
                    className="text-sm border rounded-lg px-2 py-1 bg-white"
                  >
                    {FASCE_COLORS.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                  
                  {fasce.length > 1 && (
                    <button
                      onClick={() => removeFascia(index)}
                      className="p-1 text-red-500 hover:bg-red-100 rounded"
                      data-testid={`fascia-remove-${index}`}
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
              
              {/* Selettore posizioni */}
              <div className="mb-3">
                <label className={`text-xs font-medium ${colorClasses.textClass} mb-2 block`}>
                  Piazzamenti che vanno in questa fascia:
                </label>
                <div className="flex flex-wrap gap-2">
                  {availablePositions.map(pos => {
                    const isSelected = fascia.positions.includes(pos);
                    const isUsedByOther = !isSelected && usedPositions.includes(pos);
                    
                    return (
                      <button
                        key={pos}
                        onClick={() => togglePosition(index, pos)}
                        disabled={isUsedByOther}
                        className={`
                          w-10 h-10 rounded-lg font-bold text-sm transition-all
                          ${isSelected 
                            ? `${colorClasses.bgClass} ${colorClasses.textClass} border-2 ${colorClasses.borderClass} shadow-md` 
                            : isUsedByOther
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-white text-gray-600 border border-gray-300 hover:border-gray-400'}
                        `}
                        data-testid={`fascia-${index}-pos-${pos}`}
                      >
                        {pos}°
                      </button>
                    );
                  })}
                </div>
              </div>
              
              {/* Info giocatori */}
              <div className={`text-sm ${colorClasses.textClass} flex items-center gap-2`}>
                <span>👥</span>
                <span>
                  {getPlayersPerFascia(fascia)} giocatori 
                  ({fascia.positions.length} posizion{fascia.positions.length === 1 ? 'e' : 'i'} × {numGroups} giron{numGroups === 1 ? 'e' : 'i'})
                </span>
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Pulsante aggiungi fascia */}
      {unusedPositions.length > 0 && (
        <button
          onClick={addFascia}
          className="w-full p-4 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-gray-400 hover:text-gray-600 transition-colors"
          data-testid="btn-add-fascia"
        >
          ➕ Aggiungi Fascia
        </button>
      )}
      
      {/* Warning posizioni non assegnate */}
      {unusedPositions.length > 0 && (
        <div className="bg-yellow-50 p-4 rounded-xl border border-yellow-200">
          <p className="text-sm text-yellow-800">
            ⚠️ Posizioni non assegnate: {unusedPositions.map(p => `${p}°`).join(', ')}
            <br />
            <span className="text-xs text-yellow-600">
              Questi giocatori non parteciperanno alla fase eliminazione
            </span>
          </p>
        </div>
      )}
      
      {/* Riepilogo */}
      {fasce.length > 0 && (
        <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
          <h5 className="font-medium text-blue-800 mb-2">📊 Riepilogo Fasce</h5>
          <div className="grid grid-cols-2 gap-2 text-sm">
            {fasce.map((fascia, i) => (
              <div key={i} className="flex justify-between">
                <span className="text-blue-700">{fascia.name}:</span>
                <span className="font-medium text-blue-800">{getPlayersPerFascia(fascia)} giocatori</span>
              </div>
            ))}
            <div className="col-span-2 pt-2 border-t border-blue-200 flex justify-between font-bold">
              <span className="text-blue-700">Totale in eliminazione:</span>
              <span className="text-blue-800">{fasce.reduce((sum, f) => sum + getPlayersPerFascia(f), 0)} giocatori</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// 🧙 WIZARD COMPONENT
// ============================================

export const TorneoWizardV3 = ({ 
  onComplete, 
  onCancel, 
  token,
  existingMembers = [] 
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    type: 'torneo',
    start_date: '', // Data inizio torneo
    end_date: '',   // Data fine (opzionale)
    group_stage: {
      enabled: true,
      number_of_groups: 1,
      home_away: false,
      points_win: 3,
      points_draw: 1,
      points_loss: 0,
      distribution_method: 'automatic' // 'automatic' | 'manual'
    },
    qualification: {
      method: 'direct',
      direct_positions: 2,
      transversal_categories: ['second'],
      transversal_slots: 4,
      normalization: 'points_per_game'
    },
    fasce: {
      enabled: false,
      config: [
        { name: 'Oro', positions: [1], color: 'yellow' },
        { name: 'Argento', positions: [2], color: 'gray' }
      ]
    },
    knockout: {
      enabled: true,
      format: 'single',
      third_place_match: false,
      home_away_rounds: ['ottavi', 'quarti', 'semifinale'] // Turni con A/R (esclusa finale di default)
    },
    participants: [],
    // Date giornate (per campionato) - formato: { 1: '2026-02-15', 2: '2026-02-22', ... }
    giornate_dates: {},
    // Assegnazione manuale gironi - formato: { 'A': ['id1', 'id2'], 'B': ['id3', 'id4'] }
    manual_groups: {}
  });
  
  const [newParticipantName, setNewParticipantName] = useState('');
  const [newParticipantTeam, setNewParticipantTeam] = useState('');
  const [selectedGroupForAssignment, setSelectedGroupForAssignment] = useState('A');

  // Fetch recommended config when participants change
  const fetchRecommendedConfig = useCallback(async (count) => {
    if (count < 2) return;
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/clubs/tornei-v3/recommended-config/${count}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        // Solo suggerisci, non sovrascrivere
        console.log('Recommended config:', data.recommended_config);
      }
    } catch (error) {
      console.error('Error fetching recommended config:', error);
    }
  }, [token]);

  // Validate configuration
  const validateConfig = useCallback(async () => {
    setIsValidating(true);
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/clubs/tornei-v3/validate-config`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            config: {
              type: formData.type,
              group_stage: formData.group_stage,
              qualification: formData.qualification,
              knockout: formData.knockout,
              fasce: formData.fasce  // Include fasce config for validation
            },
            participants_count: formData.participants.length
          })
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        setValidationResult(result);
        return result.valid;
      }
    } catch (error) {
      console.error('Validation error:', error);
    } finally {
      setIsValidating(false);
    }
    
    return false;
  }, [token, formData]);

  // Funzione per saltare step non necessari
  const getNextStep = (current) => {
    const next = current + 1;
    // Se step 6 e distribuzione automatica, salta al 7
    if (next === 6 && formData.group_stage.distribution_method === 'automatic') {
      return 7;
    }
    return next;
  };
  
  const getPrevStep = (current) => {
    const prev = current - 1;
    // Se step 7 e distribuzione automatica, torna al 5
    if (prev === 6 && formData.group_stage.distribution_method === 'automatic') {
      return 5;
    }
    return prev;
  };

  // Handle step navigation
  const nextStep = async () => {
    // Validate before going to summary step
    if (currentStep === 5 || (currentStep === 6 && formData.group_stage.distribution_method === 'manual')) {
      const isValid = await validateConfig();
      if (!isValid) return;
    }
    
    if (currentStep < WIZARD_STEPS.length) {
      const next = getNextStep(currentStep);
      setCurrentStep(next);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      const prev = getPrevStep(currentStep);
      setCurrentStep(prev);
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/clubs/tornei-v3`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: formData.name,
            start_date: formData.start_date || null,
            end_date: formData.end_date || null,
            config: {
              type: formData.type,
              group_stage: formData.group_stage,
              qualification: formData.qualification,
              fasce: formData.fasce,
              knockout: formData.knockout
            },
            participants: formData.participants,
            manual_groups: formData.group_stage.distribution_method === 'manual' ? formData.manual_groups : null
          })
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        onComplete(result);
      } else {
        const error = await response.json();
        alert(`Errore: ${error.detail?.message || error.detail || 'Errore sconosciuto'}`);
      }
    } catch (error) {
      console.error('Submit error:', error);
      alert('Errore nella creazione del torneo');
    }
  };

  // Add participant
  const addParticipant = () => {
    if (!newParticipantName.trim()) return;
    
    const newParticipant = {
      id: `temp_${Date.now()}`,
      name: newParticipantName.trim(),
      team_name: newParticipantTeam.trim() || ''
    };
    
    setFormData(prev => ({
      ...prev,
      participants: [...prev.participants, newParticipant]
    }));
    setNewParticipantName('');
    setNewParticipantTeam('');
  };

  // Remove participant
  const removeParticipant = (id) => {
    setFormData(prev => ({
      ...prev,
      participants: prev.participants.filter(p => p.id !== id)
    }));
  };

  // Add from existing members
  const addFromMembers = (member) => {
    if (formData.participants.find(p => p.id === member.id)) return;
    
    setFormData(prev => ({
      ...prev,
      participants: [...prev.participants, { 
        id: member.id, 
        name: member.nome || member.name,
        team_name: member.squadra_preferita || ''
      }]
    }));
  };

  // Update group count based on participants
  useEffect(() => {
    const count = formData.participants.length;
    if (count >= 4) {
      // Auto-suggest groups
      let suggestedGroups = 1;
      if (count >= 8) suggestedGroups = 2;
      if (count >= 12) suggestedGroups = 3;
      if (count >= 16) suggestedGroups = 4;
      if (count >= 24) suggestedGroups = 6;
      if (count >= 32) suggestedGroups = 8;
      
      // Solo suggerisci se non già modificato manualmente
      if (formData.group_stage.number_of_groups === 1 && suggestedGroups > 1) {
        setFormData(prev => ({
          ...prev,
          group_stage: { ...prev.group_stage, number_of_groups: suggestedGroups }
        }));
      }
    }
  }, [formData.participants.length]);

  // ============================================
  // 🎨 RENDER STEPS
  // ============================================

  const renderStep1_Type = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-2xl font-bold text-gray-800">Che tipo di evento vuoi creare?</h3>
        <p className="text-gray-500 mt-2">Scegli il formato del tuo torneo</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {TOURNAMENT_TYPES.map(type => (
          <button
            key={type.value}
            disabled={type.disabled}
            onClick={() => {
              setFormData(prev => ({
                ...prev,
                type: type.value,
                knockout: {
                  ...prev.knockout,
                  enabled: type.value !== 'campionato'
                }
              }));
            }}
            className={`
              relative p-6 rounded-xl border-2 transition-all duration-300
              ${formData.type === type.value 
                ? 'border-blue-500 bg-blue-50 shadow-lg scale-105' 
                : 'border-gray-200 hover:border-gray-300 hover:shadow'}
              ${type.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
          >
            <div className={`
              w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br ${type.color}
              flex items-center justify-center text-3xl text-white shadow-lg
            `}>
              {type.icon}
            </div>
            <h4 className="font-bold text-lg text-gray-800">{type.label}</h4>
            <p className="text-sm text-gray-500 mt-2">{type.desc}</p>
            
            {type.disabled && (
              <span className="absolute top-2 right-2 text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded-full">
                Prossimamente
              </span>
            )}
            
            {formData.type === type.value && (
              <div className="absolute -top-2 -right-2 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white">
                ✓
              </div>
            )}
          </button>
        ))}
      </div>
      
      {/* Nome torneo */}
      <div className="mt-8">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Nome Torneo *
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          placeholder="es. Campionato Primavera 2026"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          data-testid="input-tournament-name"
        />
      </div>

      {/* Date Torneo */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            📅 Data Inizio
          </label>
          <input
            type="date"
            value={formData.start_date}
            onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            data-testid="input-start-date"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            📅 Data Fine <span className="text-gray-400">(opzionale)</span>
          </label>
          <input
            type="date"
            value={formData.end_date}
            onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
            min={formData.start_date}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            data-testid="input-end-date"
          />
        </div>
      </div>
      
      {formData.type === 'campionato' && formData.start_date && (
        <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-sm text-blue-700">
            💡 <strong>Campionato:</strong> Dopo aver creato il torneo, potrai assegnare una data specifica ad ogni giornata dal calendario.
          </p>
        </div>
      )}
    </div>
  );

  const renderStep2_Groups = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-2xl font-bold text-gray-800">
          {formData.type === 'campionato' ? 'Configurazione Campionato' : 'Configurazione Gironi'}
        </h3>
        <p className="text-gray-500 mt-2">
          {formData.type === 'campionato' ? 'Imposta le giornate del campionato' : 'Imposta la fase a gironi'}
        </p>
      </div>
      
      {/* Abilita gironi - NASCOSTO per Campionato (sempre abilitato automaticamente) */}
      {formData.type !== 'campionato' && (
        <div className="bg-gray-50 p-6 rounded-xl">
          <label className="flex items-center justify-between cursor-pointer">
            <div>
              <span className="font-medium text-gray-800">Fase a Gironi</span>
              <p className="text-sm text-gray-500">Abilita la fase preliminare a gironi</p>
            </div>
            <div className={`
              w-14 h-8 rounded-full p-1 transition-colors duration-300
              ${formData.group_stage.enabled ? 'bg-blue-500' : 'bg-gray-300'}
            `}>
              <div className={`
                w-6 h-6 bg-white rounded-full shadow transition-transform duration-300
                ${formData.group_stage.enabled ? 'translate-x-6' : 'translate-x-0'}
              `} />
            </div>
          </label>
        </div>
      )}
      
      {(formData.group_stage.enabled || formData.type === 'campionato') && (
        <>
          {/* Numero gironi */}
          <div className="bg-white p-6 rounded-xl border border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-4">
              Numero di Gironi
            </label>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min="1"
                max="16"
                value={formData.group_stage.number_of_groups}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  group_stage: { ...prev.group_stage, number_of_groups: parseInt(e.target.value) }
                }))}
                className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <span className="w-16 text-center text-2xl font-bold text-blue-600">
                {formData.group_stage.number_of_groups}
              </span>
            </div>
            
            {formData.participants.length > 0 && (
              <p className="mt-3 text-sm text-gray-500">
                📊 Con {formData.participants.length} partecipanti: ~{Math.ceil(formData.participants.length / formData.group_stage.number_of_groups)} per girone
              </p>
            )}
          </div>
          
          {/* Andata/Ritorno */}
          <div className="bg-gray-50 p-6 rounded-xl">
            <label 
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setFormData(prev => ({
                ...prev,
                group_stage: { ...prev.group_stage, home_away: !prev.group_stage.home_away }
              }))}
              data-testid="toggle-home-away-groups"
            >
              <div>
                <span className="font-medium text-gray-800">Andata e Ritorno</span>
                <p className="text-sm text-gray-500">Raddoppia le partite (tutti contro tutti x2)</p>
              </div>
              <div className={`
                w-14 h-8 rounded-full p-1 transition-colors duration-300
                ${formData.group_stage.home_away ? 'bg-blue-500' : 'bg-gray-300'}
              `}>
                <div className={`
                  w-6 h-6 bg-white rounded-full shadow transition-transform duration-300
                  ${formData.group_stage.home_away ? 'translate-x-6' : 'translate-x-0'}
                `} />
              </div>
            </label>
          </div>
          
          {/* Punti */}
          <div className="bg-white p-6 rounded-xl border border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-4">
              Sistema Punteggio
            </label>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-xs text-gray-500">Vittoria</label>
                <input
                  type="number"
                  min="0"
                  max="10"
                  value={formData.group_stage.points_win}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    group_stage: { ...prev.group_stage, points_win: parseInt(e.target.value) }
                  }))}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-center font-bold text-green-600"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500">Pareggio</label>
                <input
                  type="number"
                  min="0"
                  max="10"
                  value={formData.group_stage.points_draw}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    group_stage: { ...prev.group_stage, points_draw: parseInt(e.target.value) }
                  }))}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-center font-bold text-yellow-600"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500">Sconfitta</label>
                <input
                  type="number"
                  min="0"
                  max="10"
                  value={formData.group_stage.points_loss}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    group_stage: { ...prev.group_stage, points_loss: parseInt(e.target.value) }
                  }))}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-center font-bold text-red-600"
                />
              </div>
            </div>
          </div>
          
          {/* Metodo Distribuzione Gironi */}
          <div className="bg-white p-6 rounded-xl border border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-4">
              Formazione Gironi
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setFormData(prev => ({
                  ...prev,
                  group_stage: { ...prev.group_stage, distribution_method: 'automatic' },
                  manual_groups: {}
                }))}
                className={`p-4 rounded-xl border-2 text-left transition-all ${
                  formData.group_stage.distribution_method === 'automatic'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">🎲</span>
                  <div>
                    <p className="font-semibold text-gray-800">Automatico</p>
                    <p className="text-sm text-gray-500">Distribuzione bilanciata (snake)</p>
                  </div>
                </div>
              </button>
              
              <button
                type="button"
                onClick={() => setFormData(prev => ({
                  ...prev,
                  group_stage: { ...prev.group_stage, distribution_method: 'manual' }
                }))}
                className={`p-4 rounded-xl border-2 text-left transition-all ${
                  formData.group_stage.distribution_method === 'manual'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">✋</span>
                  <div>
                    <p className="font-semibold text-gray-800">Manuale</p>
                    <p className="text-sm text-gray-500">Scegli tu chi va in ogni girone</p>
                  </div>
                </div>
              </button>
            </div>
            
            {formData.group_stage.distribution_method === 'manual' && (
              <p className="mt-4 text-sm text-blue-600 bg-blue-50 p-3 rounded-lg">
                ℹ️ Dopo aver aggiunto i partecipanti, potrai assegnarli manualmente ai gironi nello Step 6
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );

  const renderStep3_Qualification = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-2xl font-bold text-gray-800">Metodo Qualificazione</h3>
        <p className="text-gray-500 mt-2">Come vengono selezionati i qualificati alla fase successiva?</p>
      </div>
      
      <div className="space-y-4">
        {QUALIFICATION_METHODS.map(method => (
          <button
            key={method.value}
            disabled={method.disabled}
            onClick={() => {
              // Se è placement, forza 2 gironi
              if (method.value === 'placement' && formData.group_stage.number_of_groups !== 2) {
                setFormData(prev => ({
                  ...prev,
                  group_stage: { ...prev.group_stage, number_of_groups: 2 },
                  qualification: { ...prev.qualification, method: method.value }
                }));
              } else {
                setFormData(prev => ({
                  ...prev,
                  qualification: { ...prev.qualification, method: method.value },
                  fasce: {
                    ...prev.fasce,
                    enabled: method.value === 'fasce',
                    config: method.value === 'fasce' && prev.fasce.config.length === 0 
                      ? [
                          { name: 'Oro', positions: [1], color: 'yellow' },
                          { name: 'Argento', positions: [2], color: 'gray' }
                        ]
                      : prev.fasce.config
                  }
                }));
              }
            }}
            className={`
              w-full p-5 rounded-xl border-2 text-left transition-all duration-300
              ${formData.qualification.method === method.value 
                ? 'border-blue-500 bg-blue-50 shadow-lg' 
                : 'border-gray-200 hover:border-gray-300 hover:shadow'}
              ${method.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              ${method.requiresGroups && formData.group_stage.number_of_groups !== method.requiresGroups 
                ? 'border-orange-300 bg-orange-50' : ''}
            `}
          >
            <div className="flex items-center gap-4">
              <span className="text-3xl">{method.icon}</span>
              <div className="flex-1">
                <h4 className="font-bold text-gray-800">{method.label}</h4>
                <p className="text-sm text-gray-500">{method.desc}</p>
                {method.requiresGroups && formData.group_stage.number_of_groups !== method.requiresGroups && (
                  <p className="text-xs text-orange-600 mt-1">
                    ⚠️ Richiede esattamente {method.requiresGroups} gironi (attualmente: {formData.group_stage.number_of_groups})
                  </p>
                )}
              </div>
              {formData.qualification.method === method.value && (
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white">
                  ✓
                </div>
              )}
              {method.disabled && (
                <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded-full">
                  Fase 2
                </span>
              )}
            </div>
          </button>
        ))}
      </div>
      
      {/* Opzioni per metodo direct */}
      {formData.qualification.method === 'direct' && (
        <div className="bg-white p-6 rounded-xl border border-gray-200 mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-4">
            Posizioni che qualificano (per girone)
          </label>
          <div className="flex items-center gap-4">
            <input
              type="range"
              min="1"
              max="8"
              value={formData.qualification.direct_positions}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                qualification: { ...prev.qualification, direct_positions: parseInt(e.target.value) }
              }))}
              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <span className="w-16 text-center text-2xl font-bold text-blue-600">
              {formData.qualification.direct_positions}
            </span>
          </div>
          <p className="mt-3 text-sm text-gray-500">
            🎯 I primi {formData.qualification.direct_positions} di ogni girone passano 
            ({formData.group_stage.number_of_groups * formData.qualification.direct_positions} qualificati totali)
          </p>
        </div>
      )}
      
      {/* Opzioni per metodo transversal */}
      {formData.qualification.method === 'transversal' && (
        <div className="space-y-4 mt-6">
          <div className="bg-white p-6 rounded-xl border border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-4">
              Qualificati diretti per girone
            </label>
            <input
              type="range"
              min="1"
              max="4"
              value={formData.qualification.direct_positions}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                qualification: { ...prev.qualification, direct_positions: parseInt(e.target.value) }
              }))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between mt-2 text-sm text-gray-500">
              <span>1</span>
              <span className="font-bold text-blue-600">{formData.qualification.direct_positions}</span>
              <span>4</span>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-xl border border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-4">
              Slot per migliori secondi/terzi
            </label>
            <input
              type="range"
              min="0"
              max="8"
              value={formData.qualification.transversal_slots}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                qualification: { ...prev.qualification, transversal_slots: parseInt(e.target.value) }
              }))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between mt-2 text-sm text-gray-500">
              <span>0</span>
              <span className="font-bold text-blue-600">{formData.qualification.transversal_slots}</span>
              <span>8</span>
            </div>
          </div>
          
          <div className="bg-blue-50 p-4 rounded-xl">
            <p className="text-sm text-blue-800">
              📊 Totale qualificati: {formData.group_stage.number_of_groups * formData.qualification.direct_positions + formData.qualification.transversal_slots}
            </p>
          </div>
        </div>
      )}

      {/* Opzioni per metodo fasce */}
      {formData.qualification.method === 'fasce' && (
        <FasceConfigSection 
          formData={formData} 
          setFormData={setFormData} 
        />
      )}

      {/* Opzioni per metodo placement (Torneo Fast) */}
      {formData.qualification.method === 'placement' && (
        <div className="space-y-4 mt-6">
          <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-6 rounded-xl border border-orange-200">
            <h4 className="font-bold text-orange-800 mb-3">⚡ Torneo Fast - Finali per Piazzamento</h4>
            <p className="text-sm text-orange-700 mb-4">
              Formato veloce con 2 gironi. Al termine della fase a gironi, si giocano le finali:
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white/70 p-3 rounded-lg border border-orange-200">
                <span className="text-lg">🥇</span>
                <p className="text-sm font-medium text-gray-700">1° Girone A vs 1° Girone B</p>
                <p className="text-xs text-gray-500">Finale 1°/2° posto</p>
              </div>
              <div className="bg-white/70 p-3 rounded-lg border border-orange-200">
                <span className="text-lg">🥈</span>
                <p className="text-sm font-medium text-gray-700">2° Girone A vs 2° Girone B</p>
                <p className="text-xs text-gray-500">Finale 3°/4° posto</p>
              </div>
              <div className="bg-white/70 p-3 rounded-lg border border-orange-200">
                <span className="text-lg">🥉</span>
                <p className="text-sm font-medium text-gray-700">3° Girone A vs 3° Girone B</p>
                <p className="text-xs text-gray-500">Finale 5°/6° posto</p>
              </div>
              <div className="bg-white/70 p-3 rounded-lg border border-orange-200">
                <span className="text-lg">🏅</span>
                <p className="text-sm font-medium text-gray-700">4° Girone A vs 4° Girone B</p>
                <p className="text-xs text-gray-500">Finale 7°/8° posto</p>
              </div>
            </div>
          </div>
          
          {formData.group_stage.number_of_groups !== 2 && (
            <div className="bg-red-50 p-4 rounded-xl border border-red-200">
              <p className="text-sm text-red-700">
                ⚠️ <strong>Attenzione:</strong> Questo metodo richiede esattamente 2 gironi.
                <br />Attualmente hai {formData.group_stage.number_of_groups} giron{formData.group_stage.number_of_groups === 1 ? 'e' : 'i'}.
                <button 
                  onClick={() => setFormData(prev => ({
                    ...prev,
                    group_stage: { ...prev.group_stage, number_of_groups: 2 }
                  }))}
                  className="ml-2 text-red-800 underline font-medium hover:text-red-900"
                >
                  Imposta 2 gironi
                </button>
              </p>
            </div>
          )}
          
          <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
            <p className="text-sm text-blue-800">
              📊 Con {Math.ceil(formData.participants.length / 2) || 4} giocatori per girone, 
              avrai {Math.ceil(formData.participants.length / 2) || 4} finali per piazzamento
            </p>
          </div>
        </div>
      )}
    </div>
  );

  const renderStep4_Knockout = () => {
    // Stima il numero di partecipanti per capire quali turni saranno disponibili
    const qualifiedEstimate = formData.qualification.method === 'direct'
      ? formData.group_stage.number_of_groups * formData.qualification.direct_positions
      : formData.qualification.method === 'transversal'
        ? formData.group_stage.number_of_groups * formData.qualification.direct_positions + formData.qualification.transversal_slots
        : formData.participants.length;
    
    // Filtra i turni disponibili in base al numero stimato di qualificati
    const availableRounds = KNOCKOUT_ROUNDS.filter(r => qualifiedEstimate >= r.minTeams);

    // Toggle turno per A/R
    const toggleHomeAwayRound = (roundValue) => {
      setFormData(prev => {
        const current = prev.knockout.home_away_rounds || [];
        const newRounds = current.includes(roundValue)
          ? current.filter(r => r !== roundValue)
          : [...current, roundValue];
        return {
          ...prev,
          knockout: { ...prev.knockout, home_away_rounds: newRounds }
        };
      });
    };

    return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-2xl font-bold text-gray-800">Fase Eliminazione</h3>
        <p className="text-gray-500 mt-2">Configura la fase ad eliminazione diretta</p>
      </div>
      
      {formData.type === 'campionato' ? (
        <div className="bg-yellow-50 p-6 rounded-xl border border-yellow-200 text-center">
          <span className="text-4xl">🏆</span>
          <p className="mt-4 text-yellow-800">
            Hai scelto "Campionato": non c'è fase eliminazione.
            <br />La classifica finale sarà quella dei gironi.
          </p>
        </div>
      ) : (
        <>
          {/* Formato */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              Formato Partite
            </label>
            <div className="grid grid-cols-3 gap-3">
              {KNOCKOUT_FORMATS.map(format => (
                <button
                  key={format.value}
                  disabled={format.disabled}
                  onClick={() => setFormData(prev => ({
                    ...prev,
                    knockout: { ...prev.knockout, format: format.value }
                  }))}
                  className={`
                    p-4 rounded-xl border-2 text-center transition-all
                    ${formData.knockout.format === format.value 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300'}
                    ${format.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  `}
                  data-testid={`knockout-format-${format.value}`}
                >
                  <span className="text-2xl">{format.icon}</span>
                  <p className="mt-2 text-sm font-medium">{format.label}</p>
                  {format.disabled && (
                    <span className="text-xs text-gray-400">Prossimamente</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Sezione Andata/Ritorno - Selezione turni */}
          {formData.knockout.format === 'home_away' && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200 space-y-4">
              <div>
                <h4 className="font-bold text-blue-800 mb-2">🔄 Configurazione Andata/Ritorno</h4>
                <p className="text-sm text-blue-700">
                  Seleziona i turni che si giocheranno in Andata e Ritorno. 
                  Il passaggio del turno si decide con la <strong>somma dei gol</strong> delle due partite.
                </p>
              </div>

              {/* Info box */}
              <div className="bg-white/70 p-4 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800">
                  <strong>Come funziona:</strong> Ogni sfida si gioca in due partite. 
                  Chi segna più gol in totale passa il turno.
                  In caso di parità, l'admin segnerà manualmente il vincitore dopo i rigori.
                </p>
              </div>

              {/* Selezione turni */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-blue-800">
                  Turni con Andata/Ritorno:
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {KNOCKOUT_ROUNDS.map(round => {
                    const isAvailable = qualifiedEstimate >= round.minTeams;
                    const isSelected = (formData.knockout.home_away_rounds || []).includes(round.value);
                    
                    return (
                      <button
                        key={round.value}
                        disabled={!isAvailable}
                        onClick={() => toggleHomeAwayRound(round.value)}
                        className={`
                          p-3 rounded-lg border-2 text-left transition-all
                          ${isSelected
                            ? 'border-blue-500 bg-blue-100 text-blue-800'
                            : isAvailable
                              ? 'border-gray-200 bg-white hover:border-blue-300 text-gray-700'
                              : 'border-gray-100 bg-gray-50 text-gray-400 cursor-not-allowed'}
                        `}
                        data-testid={`home-away-round-${round.value}`}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`
                            w-5 h-5 rounded border-2 flex items-center justify-center text-xs
                            ${isSelected 
                              ? 'border-blue-500 bg-blue-500 text-white' 
                              : 'border-gray-300'}
                          `}>
                            {isSelected && '✓'}
                          </div>
                          <span className="text-sm font-medium">{round.label}</span>
                        </div>
                        {!isAvailable && (
                          <p className="text-xs text-gray-400 mt-1 ml-7">
                            Richiede {round.minTeams}+ qualificati
                          </p>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Riepilogo */}
              {(formData.knockout.home_away_rounds || []).length > 0 && (
                <div className="bg-blue-100/50 p-3 rounded-lg">
                  <p className="text-sm text-blue-800">
                    📋 <strong>Riepilogo:</strong> {(formData.knockout.home_away_rounds || []).map(r => 
                      KNOCKOUT_ROUNDS.find(kr => kr.value === r)?.label
                    ).join(', ')} saranno in A/R
                    {!(formData.knockout.home_away_rounds || []).includes('finale') && (
                      <span className="text-blue-600"> (Finale in partita secca)</span>
                    )}
                  </p>
                </div>
              )}
            </div>
          )}
          
          {/* Finale 3°/4° posto */}
          <div className="bg-gray-50 p-6 rounded-xl">
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <span className="font-medium text-gray-800">Finale 3°/4° Posto</span>
                <p className="text-sm text-gray-500">I perdenti delle semifinali si sfidano per il 3° posto</p>
              </div>
              <div className={`
                w-14 h-8 rounded-full p-1 transition-colors duration-300
                ${formData.knockout.third_place_match ? 'bg-blue-500' : 'bg-gray-300'}
              `}>
                <div className={`
                  w-6 h-6 bg-white rounded-full shadow transition-transform duration-300
                  ${formData.knockout.third_place_match ? 'translate-x-6' : 'translate-x-0'}
                `} />
              </div>
            </label>
          </div>
        </>
      )}
    </div>
  )};


  // Stato per il tab partecipanti
  const [participantTab, setParticipantTab] = useState('soci'); // 'soci' o 'ospite'
  const [socioSearch, setSocioSearch] = useState('');
  const [selectedSoci, setSelectedSoci] = useState({}); // { socioId: { selected: bool, team: string } }
  const [guestName, setGuestName] = useState('');
  const [guestTeam, setGuestTeam] = useState('');

  // Filtra soci per ricerca
  const filteredSoci = existingMembers.filter(m => {
    const name = (m.nome || m.name || '').toLowerCase();
    const search = socioSearch.toLowerCase();
    return name.includes(search) && !formData.participants.find(p => p.id === m.id);
  });

  // Toggle selezione socio
  const toggleSocioSelection = (socio) => {
    setSelectedSoci(prev => ({
      ...prev,
      [socio.id]: prev[socio.id]?.selected 
        ? { ...prev[socio.id], selected: false }
        : { selected: true, team: prev[socio.id]?.team || '' }
    }));
  };

  // Aggiorna squadra per socio selezionato
  const updateSocioTeam = (socioId, team) => {
    setSelectedSoci(prev => ({
      ...prev,
      [socioId]: { ...prev[socioId], team }
    }));
  };

  // Aggiungi tutti i soci selezionati
  const addSelectedSoci = () => {
    const sociToAdd = existingMembers.filter(m => selectedSoci[m.id]?.selected);
    sociToAdd.forEach(socio => {
      const teamName = selectedSoci[socio.id]?.team || '';
      setFormData(prev => ({
        ...prev,
        participants: [...prev.participants, {
          id: socio.id,
          name: socio.nome || socio.name,
          team_name: teamName,
          tipo: 'socio',
          logo_url: socio.logo_url || null
        }]
      }));
    });
    setSelectedSoci({});
    setSocioSearch('');
  };

  // Aggiungi ospite
  const addGuest = () => {
    if (!guestName.trim()) return;
    setFormData(prev => ({
      ...prev,
      participants: [...prev.participants, {
        id: `guest_${Date.now()}`,
        name: guestName.trim(),
        team_name: guestTeam.trim(),
        tipo: 'ospite',
        logo_url: null
      }]
    }));
    setGuestName('');
    setGuestTeam('');
  };

  // Conta soci selezionati
  const selectedSociCount = Object.values(selectedSoci).filter(s => s?.selected).length;

  const renderStep5_Participants = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-gray-800">👥 Partecipanti</h3>
        <p className="text-gray-500 mt-2">Aggiungi soci del club o ospiti esterni</p>
      </div>
      
      {/* Partecipanti selezionati - SEMPRE VISIBILI IN PRIMO PIANO */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-4">
        <div className="flex justify-between items-center mb-3">
          <span className="font-bold text-blue-800">
            🏆 Partecipanti Selezionati ({formData.participants.length})
          </span>
          {formData.participants.length >= 2 && (
            <span className="text-green-600 text-sm font-medium bg-green-100 px-2 py-1 rounded-full">✓ Pronto</span>
          )}
        </div>
        
        {formData.participants.length === 0 ? (
          <div className="text-center py-4 text-gray-400">
            <span className="text-3xl">👥</span>
            <p className="mt-1 text-sm">Nessun partecipante aggiunto</p>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
            {formData.participants.map((p, index) => (
              <div 
                key={p.id}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
                  p.tipo === 'ospite' 
                    ? 'bg-orange-50 border-orange-300' 
                    : 'bg-white border-blue-200'
                }`}
              >
                <span className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                  {index + 1}
                </span>
                <div className="flex flex-col">
                  <span className="font-medium text-sm">
                    {p.team_name || p.name}
                  </span>
                  {p.team_name && (
                    <span className="text-xs text-gray-500">({p.name})</span>
                  )}
                </div>
                <span className={`text-xs px-1.5 py-0.5 rounded ${
                  p.tipo === 'ospite' 
                    ? 'bg-orange-200 text-orange-700' 
                    : 'bg-blue-100 text-blue-700'
                }`}>
                  {p.tipo === 'ospite' ? '👤' : '🏠'}
                </span>
                <button
                  onClick={() => removeParticipant(p.id)}
                  className="text-red-400 hover:text-red-600 ml-1"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
        
        {formData.participants.length < 2 && (
          <p className="text-yellow-700 text-xs mt-2 bg-yellow-100 p-2 rounded">
            ⚠️ Servono almeno 2 partecipanti
          </p>
        )}
      </div>
      
      {/* Tab Soci / Ospite */}
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setParticipantTab('soci')}
            className={`flex-1 px-4 py-3 font-medium text-sm transition-colors ${
              participantTab === 'soci'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
            }`}
          >
            🏠 Soci del Club
          </button>
          <button
            onClick={() => setParticipantTab('ospite')}
            className={`flex-1 px-4 py-3 font-medium text-sm transition-colors ${
              participantTab === 'ospite'
                ? 'bg-orange-500 text-white'
                : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
            }`}
          >
            👤 Ospite Esterno
          </button>
        </div>
        
        <div className="p-4">
          {participantTab === 'soci' ? (
            /* TAB SOCI */
            <div className="space-y-4">
              {existingMembers.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <span className="text-4xl">📭</span>
                  <p className="mt-2">Nessun socio registrato nel club</p>
                  <p className="text-xs mt-1">Vai in Anagrafica per aggiungere i soci</p>
                </div>
              ) : (
                <>
                  {/* Barra ricerca */}
                  <div className="relative">
                    <input
                      type="text"
                      value={socioSearch}
                      onChange={(e) => setSocioSearch(e.target.value)}
                      placeholder="🔍 Cerca socio per nome..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    {socioSearch && (
                      <button
                        onClick={() => setSocioSearch('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  
                  {/* Lista soci con checkbox */}
                  <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                    {filteredSoci.length === 0 ? (
                      <div className="p-4 text-center text-gray-400 text-sm">
                        {socioSearch ? 'Nessun risultato' : 'Tutti i soci sono già stati aggiunti'}
                      </div>
                    ) : (
                      filteredSoci.map(socio => (
                        <div 
                          key={socio.id}
                          className={`flex items-center gap-3 p-3 hover:bg-gray-50 ${
                            selectedSoci[socio.id]?.selected ? 'bg-blue-50' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedSoci[socio.id]?.selected || false}
                            onChange={() => toggleSocioSelection(socio)}
                            className="w-5 h-5 rounded border-gray-300 text-blue-500 focus:ring-blue-500"
                          />
                          <div className="flex-1 min-w-0">
                            <span className="font-medium text-gray-800">
                              {socio.nome || socio.name}
                            </span>
                            {socio.email && (
                              <span className="text-xs text-gray-400 ml-2">{socio.email}</span>
                            )}
                          </div>
                          {selectedSoci[socio.id]?.selected && (
                            <input
                              type="text"
                              value={selectedSoci[socio.id]?.team || ''}
                              onChange={(e) => updateSocioTeam(socio.id, e.target.value)}
                              placeholder="Squadra..."
                              className="w-32 px-2 py-1 text-sm border border-blue-300 rounded focus:ring-2 focus:ring-blue-500"
                            />
                          )}
                        </div>
                      ))
                    )}
                  </div>
                  
                  {/* Pulsante aggiungi selezionati */}
                  {selectedSociCount > 0 && (
                    <button
                      onClick={addSelectedSoci}
                      className="w-full py-3 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition-colors"
                    >
                      ➕ Aggiungi {selectedSociCount} soci selezionati
                    </button>
                  )}
                </>
              )}
            </div>
          ) : (
            /* TAB OSPITE */
            <div className="space-y-4">
              <p className="text-sm text-gray-600 bg-orange-50 p-3 rounded-lg">
                👤 Aggiungi partecipanti esterni che non sono registrati come soci del club.
              </p>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome Ospite *
                  </label>
                  <input
                    type="text"
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addGuest()}
                    placeholder="Es: Mario Rossi"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Squadra (opzionale)
                  </label>
                  <input
                    type="text"
                    value={guestTeam}
                    onChange={(e) => setGuestTeam(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addGuest()}
                    placeholder="Es: Inter, Milan..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              </div>
              
              <button
                onClick={addGuest}
                disabled={!guestName.trim()}
                className="w-full py-3 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                ➕ Aggiungi Ospite
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Suggerimento rapido */}
      <div className="text-center text-xs text-gray-400">
        💡 Puoi mescolare soci e ospiti nello stesso torneo
      </div>
    </div>
  );

  // Step 6: Assegnazione Manuale Gironi
  const renderStep6_GroupAssignment = () => {
    const numGroups = formData.group_stage.number_of_groups;
    const groupNames = Array.from({ length: numGroups }, (_, i) => String.fromCharCode(65 + i)); // A, B, C...
    
    // Inizializza manual_groups se vuoto
    const manualGroups = formData.manual_groups || {};
    
    // Trova partecipanti già assegnati
    const assignedParticipantIds = new Set(
      Object.values(manualGroups).flat()
    );
    
    // Partecipanti non ancora assegnati
    const unassignedParticipants = formData.participants.filter(
      p => !assignedParticipantIds.has(p.id)
    );
    
    // Usa lo stato esterno per il girone selezionato
    const selectedGroup = selectedGroupForAssignment;
    const setSelectedGroup = setSelectedGroupForAssignment;
    
    // Funzione per assegnare un partecipante a un girone
    const assignToGroup = (participantId, groupName) => {
      setFormData(prev => {
        const newManualGroups = { ...prev.manual_groups };
        
        // Rimuovi da altri gironi se presente
        Object.keys(newManualGroups).forEach(g => {
          newManualGroups[g] = (newManualGroups[g] || []).filter(id => id !== participantId);
        });
        
        // Aggiungi al nuovo girone
        if (!newManualGroups[groupName]) {
          newManualGroups[groupName] = [];
        }
        newManualGroups[groupName].push(participantId);
        
        return { ...prev, manual_groups: newManualGroups };
      });
    };
    
    // Funzione per rimuovere un partecipante da un girone
    const removeFromGroup = (participantId, groupName) => {
      setFormData(prev => {
        const newManualGroups = { ...prev.manual_groups };
        if (newManualGroups[groupName]) {
          newManualGroups[groupName] = newManualGroups[groupName].filter(id => id !== participantId);
        }
        return { ...prev, manual_groups: newManualGroups };
      });
    };
    
    // Trova partecipante per ID
    const getParticipant = (id) => formData.participants.find(p => p.id === id);
    
    // Calcola quanti partecipanti dovrebbero esserci per girone (idealmente)
    const idealPerGroup = Math.ceil(formData.participants.length / numGroups);
    
    return (
      <div className="space-y-6">
        <div className="text-center mb-6">
          <h3 className="text-2xl font-bold text-gray-800">Assegna ai Gironi</h3>
          <p className="text-gray-500 mt-2">
            Clicca su un partecipante per assegnarlo al girone selezionato
          </p>
        </div>
        
        {/* Progress */}
        <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
          <div className="flex justify-between items-center">
            <span className="text-blue-800">
              Assegnati: <strong>{assignedParticipantIds.size}</strong> / {formData.participants.length}
            </span>
            <span className="text-blue-600 text-sm">
              {unassignedParticipants.length === 0 ? '✅ Tutti assegnati!' : `⏳ ${unassignedParticipants.length} da assegnare`}
            </span>
          </div>
          <div className="mt-2 w-full bg-blue-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all"
              style={{ width: `${(assignedParticipantIds.size / formData.participants.length) * 100}%` }}
            />
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Colonna sinistra: Partecipanti non assegnati */}
          <div className="bg-gray-50 p-4 rounded-xl">
            <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <span>👥</span> Da Assegnare ({unassignedParticipants.length})
            </h4>
            
            {/* Selettore girone */}
            <div className="mb-4 flex gap-2 flex-wrap">
              {groupNames.map(name => (
                <button
                  key={name}
                  onClick={() => setSelectedGroup(name)}
                  className={`px-4 py-2 rounded-lg font-bold transition-all ${
                    selectedGroup === name
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'bg-white text-gray-600 border border-gray-300 hover:border-blue-400'
                  }`}
                >
                  Girone {name}
                </button>
              ))}
            </div>
            
            {/* Lista partecipanti non assegnati */}
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {unassignedParticipants.length === 0 ? (
                <p className="text-gray-400 text-center py-4">Tutti i partecipanti sono stati assegnati</p>
              ) : (
                unassignedParticipants.map(p => (
                  <button
                    key={p.id}
                    onClick={() => assignToGroup(p.id, selectedGroup)}
                    className="w-full p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-all text-left flex items-center justify-between"
                  >
                    <div>
                      <p className="font-medium text-gray-800">{p.name}</p>
                      {p.team && <p className="text-xs text-gray-500">{p.team}</p>}
                    </div>
                    <span className="text-blue-600 text-sm">→ {selectedGroup}</span>
                  </button>
                ))
              )}
            </div>
          </div>
          
          {/* Colonna destra: Gironi */}
          <div className="space-y-4">
            {groupNames.map(groupName => {
              const groupParticipants = (manualGroups[groupName] || []).map(id => getParticipant(id)).filter(Boolean);
              const isFull = groupParticipants.length >= idealPerGroup;
              
              return (
                <div 
                  key={groupName}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    selectedGroup === groupName 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className="flex justify-between items-center mb-3">
                    <h5 className="font-bold text-lg flex items-center gap-2">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-white ${
                        selectedGroup === groupName ? 'bg-blue-600' : 'bg-gray-400'
                      }`}>
                        {groupName}
                      </span>
                      Girone {groupName}
                    </h5>
                    <span className={`text-sm px-2 py-1 rounded ${
                      isFull ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {groupParticipants.length}/{idealPerGroup}
                    </span>
                  </div>
                  
                  {groupParticipants.length === 0 ? (
                    <p className="text-gray-400 text-sm text-center py-2">Nessun partecipante</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {groupParticipants.map(p => (
                        <div 
                          key={p.id}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 rounded-full text-sm"
                        >
                          <span className="font-medium">{p.name}</span>
                          <button
                            onClick={() => removeFromGroup(p.id, groupName)}
                            className="text-red-500 hover:text-red-700 ml-1"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Warning se non tutti assegnati */}
        {unassignedParticipants.length > 0 && (
          <div className="bg-yellow-50 p-4 rounded-xl border border-yellow-200">
            <p className="text-yellow-800">
              ⚠️ Devi assegnare tutti i partecipanti prima di procedere
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderStep7_Summary = () => {
    const totalQualified = formData.qualification.method === 'direct'
      ? formData.group_stage.number_of_groups * formData.qualification.direct_positions
      : formData.group_stage.number_of_groups * formData.qualification.direct_positions + formData.qualification.transversal_slots;
    
    const matchesPerGroup = formData.participants.length > 0
      ? Math.floor(formData.participants.length / formData.group_stage.number_of_groups)
      : 0;
    const groupMatches = matchesPerGroup > 1 
      ? (matchesPerGroup * (matchesPerGroup - 1)) / 2 * (formData.group_stage.home_away ? 2 : 1)
      : 0;
    
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h3 className="text-2xl font-bold text-gray-800">Riepilogo Torneo</h3>
          <p className="text-gray-500 mt-2">Verifica la configurazione prima di creare</p>
        </div>
        
        {/* Validation errors/warnings */}
        {validationResult && (
          <div className="space-y-3">
            {validationResult.errors?.map((error, i) => (
              <div key={i} className="bg-red-50 p-4 rounded-xl border border-red-200">
                <p className="text-red-800">❌ {error}</p>
              </div>
            ))}
            {validationResult.warnings?.map((warning, i) => (
              <div key={i} className="bg-yellow-50 p-4 rounded-xl border border-yellow-200">
                <p className="text-yellow-800">⚠️ {warning}</p>
              </div>
            ))}
            {validationResult.valid && (
              <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                <p className="text-green-800">✅ Configurazione valida!</p>
              </div>
            )}
          </div>
        )}
        
        {/* Summary cards */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white p-5 rounded-xl border border-gray-200">
            <p className="text-gray-500 text-sm">Nome</p>
            <p className="font-bold text-lg">{formData.name || '(non impostato)'}</p>
          </div>
          <div className="bg-white p-5 rounded-xl border border-gray-200">
            <p className="text-gray-500 text-sm">Tipo</p>
            <p className="font-bold text-lg">
              {TOURNAMENT_TYPES.find(t => t.value === formData.type)?.label}
            </p>
          </div>
          <div className="bg-white p-5 rounded-xl border border-gray-200">
            <p className="text-gray-500 text-sm">Partecipanti</p>
            <p className="font-bold text-lg">{formData.participants.length}</p>
          </div>
          <div className="bg-white p-5 rounded-xl border border-gray-200">
            <p className="text-gray-500 text-sm">Gironi</p>
            <p className="font-bold text-lg">{formData.group_stage.number_of_groups}</p>
          </div>
          <div className="bg-white p-5 rounded-xl border border-gray-200">
            <p className="text-gray-500 text-sm">Qualificati</p>
            <p className="font-bold text-lg">{totalQualified}</p>
          </div>
          <div className="bg-white p-5 rounded-xl border border-gray-200">
            <p className="text-gray-500 text-sm">Partite/Girone</p>
            <p className="font-bold text-lg">~{groupMatches}</p>
          </div>
        </div>
        
        {/* Participants preview */}
        <div className="bg-gray-50 p-4 rounded-xl">
          <p className="font-medium text-gray-700 mb-3">Partecipanti:</p>
          <div className="flex flex-wrap gap-2">
            {formData.participants.slice(0, 20).map(p => (
              <span key={p.id} className="px-3 py-1 bg-white border border-gray-200 rounded-full text-sm">
                {p.name}
              </span>
            ))}
            {formData.participants.length > 20 && (
              <span className="px-3 py-1 bg-gray-200 rounded-full text-sm">
                +{formData.participants.length - 20} altri
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ============================================
  // 🎨 MAIN RENDER
  // ============================================

  const canProceed = () => {
    switch (currentStep) {
      case 1: return formData.name.trim().length > 0;
      case 2: return true;
      case 3: return true;
      case 4: return true;
      case 5: return formData.participants.length >= 2;
      case 6: {
        // Step 6: Assegnazione manuale - controlla solo se il metodo è manuale
        if (formData.group_stage.distribution_method === 'manual') {
          const assignedCount = Object.values(formData.manual_groups || {}).flat().length;
          return assignedCount === formData.participants.length;
        }
        return true; // Se automatico, skip
      }
      case 7: return validationResult?.valid;
      default: return true;
    }
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1: return renderStep1_Type();
      case 2: return renderStep2_Groups();
      case 3: return renderStep3_Qualification();
      case 4: return renderStep4_Knockout();
      case 5: return renderStep5_Participants();
      case 6: return renderStep6_GroupAssignment();
      case 7: return renderStep7_Summary();
      default: return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-900 to-slate-800 z-50 flex flex-col">
      {/* Header - Fullscreen */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 md:p-6 shrink-0">
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between items-center">
            <h2 className="text-xl md:text-2xl font-bold">🏆 Nuovo Torneo V3</h2>
            <button
              onClick={onCancel}
              className="text-white/80 hover:text-white text-2xl p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              ✕
            </button>
          </div>
          
          {/* Steps indicator - responsive */}
          <div className="flex justify-between mt-4 md:mt-6 overflow-x-auto pb-2">
            {WIZARD_STEPS.map((step, index) => (
              <div key={step.id} className="flex items-center shrink-0">
                <div className={`
                  w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center text-sm md:text-lg
                  ${currentStep === step.id 
                    ? 'bg-white text-blue-600 font-bold shadow-lg' 
                    : currentStep > step.id
                      ? 'bg-green-400 text-white'
                      : 'bg-white/20 text-white/60'}
                `}>
                  {currentStep > step.id ? '✓' : step.icon}
                </div>
                {index < WIZARD_STEPS.length - 1 && (
                  <div className={`
                    w-4 md:w-8 h-1 mx-1 rounded
                    ${currentStep > step.id ? 'bg-green-400' : 'bg-white/20'}
                  `} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Content - Fullscreen, centered */}
      <div className="flex-1 overflow-y-auto bg-white">
        <div className="max-w-6xl mx-auto p-4 md:p-8">
          {renderCurrentStep()}
        </div>
      </div>
      
      {/* Footer - Fixed at bottom */}
      <div className="border-t border-gray-200 p-4 flex justify-between items-center bg-gray-50 shrink-0">
        <button
          onClick={currentStep === 1 ? onCancel : prevStep}
          className="px-4 md:px-6 py-2 md:py-3 text-gray-600 hover:text-gray-800 font-medium text-sm md:text-base"
        >
          {currentStep === 1 ? 'Annulla' : '← Indietro'}
        </button>
        
        <div className="flex items-center gap-2 md:gap-3">
          {isValidating && (
            <span className="text-blue-600 text-sm">Validazione...</span>
          )}
          
          {currentStep === WIZARD_STEPS.length ? (
            <button
              onClick={handleSubmit}
              disabled={!canProceed() || isValidating}
              className="px-6 md:px-8 py-2 md:py-3 bg-green-500 text-white rounded-lg font-bold hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg text-sm md:text-base"
            >
              ✅ Crea Torneo
            </button>
          ) : (
            <button
              onClick={nextStep}
              disabled={!canProceed() || isValidating}
              className="px-6 md:px-8 py-2 md:py-3 bg-blue-500 text-white rounded-lg font-bold hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg text-sm md:text-base"
            >
              Avanti →
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================
// 📋 TOURNAMENTS LIST COMPONENT
// ============================================

export const TorneiV3List = ({ token, onSelectTournament, onSelectImportedTournament, onImport, onImportLink }) => {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [members, setMembers] = useState([]);

  const loadTournaments = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/clubs/tornei-v3`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setTournaments(data.tournaments || []);
      }
    } catch (error) {
      console.error('Error loading tournaments:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMembers = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/clubs/anagrafica`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setMembers(data.soci || []);
      }
    } catch (error) {
      console.error('Error loading members:', error);
    }
  };

  useEffect(() => {
    loadTournaments();
    loadMembers();
  }, [token]);

  const handleWizardComplete = (result) => {
    setShowWizard(false);
    loadTournaments();
    // Auto-select new tournament
    if (result.tournament_id) {
      onSelectTournament(result.tournament_id);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: { label: 'Bozza', color: 'bg-gray-100 text-gray-700' },
      groups: { label: 'Gironi', color: 'bg-blue-100 text-blue-700' },
      knockout: { label: 'Eliminazione', color: 'bg-orange-100 text-orange-700' },
      completed: { label: 'Completato', color: 'bg-green-100 text-green-700' },
      cancelled: { label: 'Annullato', color: 'bg-red-100 text-red-700' }
    };
    return badges[status] || badges.draft;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">🏆 Tornei e Campionati</h2>
          <p className="text-gray-500">Crea e gestisci tornei del tuo club</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => setShowWizard(true)}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl font-bold hover:from-blue-600 hover:to-purple-600 shadow-lg transition-all"
          >
            ➕ Nuovo Torneo
          </button>
          {onImport && (
            <button
              onClick={onImport}
              className="px-6 py-3 bg-gradient-to-r from-green-500 to-teal-500 text-white rounded-xl font-bold hover:from-green-600 hover:to-teal-600 shadow-lg transition-all flex items-center gap-2"
              data-testid="import-torneo-excel-btn"
            >
              <span>📥</span> Import Excel
            </button>
          )}
          {onImportLink && (
            <button
              onClick={onImportLink}
              className="px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-xl font-bold hover:from-orange-600 hover:to-pink-600 shadow-lg transition-all flex items-center gap-2"
              data-testid="import-torneo-link-btn"
            >
              <span>🔗</span> Import da Link
            </button>
          )}
        </div>
      </div>
      
      {/* Tournaments grid */}
      {tournaments.length === 0 ? (
        <div className="bg-gray-50 rounded-2xl p-12 text-center">
          <span className="text-6xl">🏆</span>
          <h3 className="mt-4 text-xl font-bold text-gray-700">Nessun torneo</h3>
          <p className="mt-2 text-gray-500">Crea il tuo primo torneo!</p>
          <button
            onClick={() => setShowWizard(true)}
            className="mt-6 px-8 py-3 bg-blue-500 text-white rounded-xl font-bold hover:bg-blue-600"
          >
            Crea Torneo
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tournaments.map(tournament => {
            const statusBadge = getStatusBadge(tournament.status);
            const isImported = tournament.is_imported;
            
            return (
              <div
                key={tournament.id}
                onClick={() => isImported ? onSelectImportedTournament && onSelectImportedTournament(tournament.id) : onSelectTournament(tournament.id)}
                className={`bg-white rounded-xl border p-5 transition-all cursor-pointer ${isImported ? 'border-orange-200 bg-orange-50/30 hover:shadow-md hover:border-orange-300' : 'border-gray-200 hover:shadow-lg'}`}
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center gap-2">
                    {isImported && <span className="text-orange-500">📥</span>}
                    <h3 className="font-bold text-lg text-gray-800">{tournament.name}</h3>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusBadge.color}`}>
                      {statusBadge.label}
                    </span>
                    {isImported && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                        Importato
                      </span>
                    )}
                    {isImported && tournament.status === 'completed' && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                        🔒 Non modificabile
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Imported tournament info */}
                {isImported ? (
                  <div className="space-y-2 text-sm text-gray-500">
                    <div className="flex justify-between">
                      <span>Partecipanti</span>
                      <span className="font-medium text-gray-700">{tournament.participants_count || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Partite Totali</span>
                      <span className="font-medium text-gray-700">{tournament.total_matches || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Partite Giocate</span>
                      <span className="font-medium text-green-600">{tournament.played_matches || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Tipo</span>
                      <span className="font-medium text-gray-700 capitalize">{tournament.config_type?.replace('_', ' ')}</span>
                    </div>
                    {/* Stagione */}
                    {tournament.stagione ? (
                      <div className="flex justify-between">
                        <span>Stagione</span>
                        <span className="font-medium text-gray-700">{tournament.stagione}</span>
                      </div>
                    ) : (
                      <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-1 mb-1">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-amber-500" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                          <span className="text-xs font-medium text-amber-700">Data mancante</span>
                        </div>
                        <p className="text-xs text-amber-600 mb-1.5">Inserisci la data di inizio per calcolare la stagione:</p>
                        <div className="flex gap-1.5">
                          <input
                            type="date"
                            className="flex-1 text-xs px-2 py-1 border border-amber-300 rounded bg-white"
                            data-testid={`date-fix-input-${tournament.id}`}
                            id={`date-fix-${tournament.id}`}
                          />
                          <button
                            className="px-2 py-1 bg-amber-500 text-white text-xs rounded hover:bg-amber-600 font-medium"
                            data-testid={`date-fix-save-${tournament.id}`}
                            onClick={async (e) => {
                              e.stopPropagation();
                              const dateInput = document.getElementById(`date-fix-${tournament.id}`);
                              if (!dateInput.value) return;
                              try {
                                const resp = await fetch(`${BACKEND_URL}/api/clubs/import-torneo-link/imported/update-date`, {
                                  method: 'PUT',
                                  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ tournament_id: tournament.id, data_inizio: dateInput.value })
                                });
                                const data = await resp.json();
                                if (data.success) {
                                  alert(`Stagione calcolata: ${data.stagione}`);
                                  loadTournaments();
                                }
                              } catch (err) { console.error(err); }
                            }}
                          >
                            Salva
                          </button>
                        </div>
                      </div>
                    )}
                    {tournament.source_url && (
                      <div className="pt-2 mt-2 border-t border-gray-200">
                        <a 
                          href={tournament.source_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-xs text-blue-500 hover:underline flex items-center gap-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          🔗 Fonte originale
                        </a>
                      </div>
                    )}
                    <p className="text-xs text-orange-600 mt-2">
                      👆 Clicca per vedere dettagli e classifica
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Data torneo */}
                    {tournament.start_date && (
                      <div className="mb-3 flex items-center gap-2 text-sm text-blue-600">
                        <span>📅</span>
                        <span>
                          {new Date(tournament.start_date).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' })}
                          {tournament.end_date && tournament.end_date !== tournament.start_date && (
                            <> - {new Date(tournament.end_date).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })}</>
                          )}
                        </span>
                      </div>
                    )}
                    
                    <div className="space-y-2 text-sm text-gray-500">
                      <div className="flex justify-between">
                        <span>Partecipanti</span>
                        <span className="font-medium text-gray-700">{tournament.participants_count}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Gironi</span>
                        <span className="font-medium text-gray-700">{tournament.groups_count}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Stagione</span>
                        <span className="font-medium text-gray-700">{tournament.season}</span>
                      </div>
                    </div>
                    
                    {/* Progress bars */}
                    {tournament.groups_progress && tournament.groups_progress.length > 0 && (
                      <div className="mt-4">
                        <div className="flex justify-between text-xs text-gray-500 mb-1">
                          <span>Progresso Gironi</span>
                          <span>{tournament.groups_progress[0]?.percentage || 0}%</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-blue-500 rounded-full transition-all"
                            style={{ width: `${tournament.groups_progress[0]?.percentage || 0}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
      
      {/* Wizard modal */}
      {showWizard && (
        <TorneoWizardV3
          token={token}
          existingMembers={members}
          onComplete={handleWizardComplete}
          onCancel={() => setShowWizard(false)}
        />
      )}
    </div>
  );
};

// ============================================
// 🏆 TOURNAMENT DETAIL COMPONENT
// ============================================

export const TorneoDettaglioV3 = ({ torneoId, token, onBack, onRefresh }) => {
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [savingResult, setSavingResult] = useState(null);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [selectedGroupIndex, setSelectedGroupIndex] = useState(0); // Preserve selected group across reloads
  
  // Stati per modifica torneo bozza
  const [showEditModal, setShowEditModal] = useState(false);
  const [showStartConfirm, setShowStartConfirm] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    notes: '',
    participants: []
  });
  const [isSaving, setIsSaving] = useState(false);

  const loadTournament = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${BACKEND_URL}/api/clubs/tornei-v3/${torneoId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setTournament(data);
        setError(null);
      } else {
        setError('Torneo non trovato');
      }
    } catch (err) {
      console.error('Error loading tournament:', err);
      setError('Errore caricamento torneo');
    } finally {
      setLoading(false);
    }
  }, [torneoId, token]);

  useEffect(() => {
    loadTournament();
  }, [loadTournament]);

  const showMsg = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: '', text: '' }), 4000);
  };

  // Apri modale modifica torneo
  const handleOpenEditModal = () => {
    if (tournament) {
      setEditForm({
        name: tournament.name || '',
        notes: tournament.notes || '',
        participants: tournament.participants || []
      });
      setShowEditModal(true);
    }
  };

  // Salva modifiche torneo
  const handleSaveEdit = async () => {
    if (!editForm.name.trim()) {
      showMsg('error', 'Il nome del torneo è obbligatorio');
      return;
    }
    if (editForm.participants.length < 2) {
      showMsg('error', 'Servono almeno 2 partecipanti');
      return;
    }

    setIsSaving(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/clubs/tornei-v3/${torneoId}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: editForm.name,
            notes: editForm.notes,
            participants: editForm.participants
          })
        }
      );

      if (response.ok) {
        showMsg('success', 'Torneo aggiornato con successo!');
        setShowEditModal(false);
        loadTournament();
        if (onRefresh) onRefresh();
      } else {
        const err = await response.json();
        showMsg('error', err.detail?.message || err.detail || 'Errore durante il salvataggio');
      }
    } catch (err) {
      showMsg('error', 'Errore durante il salvataggio');
    } finally {
      setIsSaving(false);
    }
  };

  // Aggiungi partecipante nel form modifica
  const addParticipantToEdit = (name, teamName = '') => {
    if (!name.trim()) return;
    const newParticipant = {
      id: `temp_${Date.now()}`,
      name: name.trim(),
      team_name: teamName.trim() || ''
    };
    setEditForm(prev => ({
      ...prev,
      participants: [...prev.participants, newParticipant]
    }));
  };

  // Rimuovi partecipante nel form modifica
  const removeParticipantFromEdit = (id) => {
    setEditForm(prev => ({
      ...prev,
      participants: prev.participants.filter(p => p.id !== id)
    }));
  };

  // Conferma avvio torneo
  const handleConfirmStart = async () => {
    setShowStartConfirm(false);
    await handleInitializeGroups();
  };

  // Inizializza gironi
  const handleInitializeGroups = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/clubs/tornei-v3/${torneoId}/initialize-groups`,
        { 
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` } 
        }
      );
      
      if (response.ok) {
        showMsg('success', 'Gironi inizializzati con successo!');
        loadTournament();
        if (onRefresh) onRefresh();
      } else {
        const err = await response.json();
        showMsg('error', err.detail || 'Errore');
      }
    } catch (err) {
      showMsg('error', 'Errore inizializzazione gironi');
    }
  };

  // Registra risultato partita girone
  const handleRecordGroupMatch = async (groupId, matchId, scoreA, scoreB) => {
    setSavingResult(matchId);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/clubs/tornei-v3/${torneoId}/groups/${groupId}/matches/${matchId}/result`,
        { 
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ score_a: scoreA, score_b: scoreB })
        }
      );
      
      if (response.ok) {
        showMsg('success', 'Risultato salvato!');
        loadTournament();
      } else {
        const err = await response.json();
        showMsg('error', err.detail || 'Errore');
      }
    } catch (err) {
      showMsg('error', 'Errore salvataggio risultato');
    } finally {
      setSavingResult(null);
    }
  };

  // Finalizza gironi
  const handleFinalizeGroups = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/clubs/tornei-v3/${torneoId}/finalize-groups`,
        { 
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` } 
        }
      );
      
      if (response.ok) {
        showMsg('success', 'Gironi finalizzati! Qualificati calcolati.');
        loadTournament();
        if (onRefresh) onRefresh();
      } else {
        const err = await response.json();
        showMsg('error', err.detail || 'Errore');
      }
    } catch (err) {
      showMsg('error', 'Errore finalizzazione gironi');
    }
  };

  // Inizializza fase eliminazione
  const handleInitializeKnockout = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/clubs/tornei-v3/${torneoId}/initialize-knockout`,
        { 
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` } 
        }
      );
      
      if (response.ok) {
        showMsg('success', 'Fase eliminazione creata!');
        loadTournament();
        if (onRefresh) onRefresh();
      } else {
        const err = await response.json();
        showMsg('error', err.detail || 'Errore');
      }
    } catch (err) {
      showMsg('error', 'Errore inizializzazione eliminazione');
    }
  };

  // Aggiorna data giornata
  const handleUpdateGiornataDate = async (giornata, date) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/clubs/tornei-v3/${torneoId}/giornata-date`,
        { 
          method: 'PUT',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ giornata, date })
        }
      );
      
      if (response.ok) {
        showMsg('success', date ? `Data giornata ${giornata} salvata!` : `Data giornata ${giornata} rimossa`);
        loadTournament();
      } else {
        const err = await response.json();
        showMsg('error', err.detail || 'Errore');
      }
    } catch (err) {
      showMsg('error', 'Errore aggiornamento data');
    }
  };

  // Registra risultato partita knockout
  const handleRecordKnockoutMatch = async (bracketKey, matchId, scoreA, scoreB, penA = null, penB = null, leg = 1) => {
    setSavingResult(matchId);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/clubs/tornei-v3/${torneoId}/brackets/${bracketKey}/matches/${matchId}/result`,
        { 
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            score_a: scoreA, 
            score_b: scoreB,
            penalties_a: penA,
            penalties_b: penB,
            leg: leg
          })
        }
      );
      
      if (response.ok) {
        showMsg('success', leg === 1 ? 'Andata salvata!' : 'Ritorno salvato!');
        loadTournament();
      } else {
        const err = await response.json();
        showMsg('error', err.detail || 'Errore');
      }
    } catch (err) {
      showMsg('error', 'Errore salvataggio risultato');
    } finally {
      setSavingResult(null);
    }
  };

  // Finalizza torneo
  const handleFinalizeTournament = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/clubs/tornei-v3/${torneoId}/finalize`,
        { 
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` } 
        }
      );
      
      if (response.ok) {
        showMsg('success', 'Torneo completato!');
        loadTournament();
        if (onRefresh) onRefresh();
      } else {
        const err = await response.json();
        showMsg('error', err.detail || 'Errore');
      }
    } catch (err) {
      showMsg('error', 'Errore finalizzazione torneo');
    }
  };

  // Elimina torneo
  const handleDeleteTournament = async () => {
    if (!window.confirm('Sei sicuro di voler eliminare questo torneo?')) return;
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/clubs/tornei-v3/${torneoId}`,
        { 
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` } 
        }
      );
      
      if (response.ok) {
        showMsg('success', 'Torneo eliminato');
        onBack();
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      showMsg('error', 'Errore eliminazione');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: { label: 'Bozza', color: 'bg-gray-500', icon: '📝' },
      groups: { label: 'Fase Gironi', color: 'bg-blue-500', icon: '📋' },
      knockout: { label: 'Eliminazione', color: 'bg-orange-500', icon: '⚔️' },
      completed: { label: 'Completato', color: 'bg-green-500', icon: '🏆' },
      cancelled: { label: 'Annullato', color: 'bg-red-500', icon: '❌' }
    };
    return badges[status] || badges.draft;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  if (error || !tournament) {
    return (
      <div className="bg-red-50 p-8 rounded-xl text-center">
        <span className="text-4xl">❌</span>
        <p className="mt-4 text-red-700">{error || 'Torneo non trovato'}</p>
        <button onClick={onBack} className="mt-4 px-6 py-2 bg-gray-200 rounded-lg hover:bg-gray-300">
          ← Torna alla lista
        </button>
      </div>
    );
  }

  const statusBadge = getStatusBadge(tournament.status);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <button 
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-lg"
              data-testid="btn-back-tornei"
            >
              ←
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">{tournament.name}</h1>
              <div className="flex items-center gap-3 mt-1">
                <span className={`px-3 py-1 rounded-full text-white text-sm ${statusBadge.color}`}>
                  {statusBadge.icon} {statusBadge.label}
                </span>
                <span className="text-gray-500 text-sm">
                  Stagione {tournament.season}
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex gap-2">
            {tournament.status === 'draft' && (
              <>
                <button
                  onClick={handleOpenEditModal}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
                  data-testid="btn-modifica-torneo"
                >
                  ✏️ Modifica
                </button>
                <button
                  onClick={() => setShowStartConfirm(true)}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-medium"
                  data-testid="btn-inizia-torneo"
                >
                  🚀 Avvia Torneo
                </button>
              </>
            )}
            {tournament.status === 'groups' && (
              <button
                onClick={handleFinalizeGroups}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 font-medium"
                data-testid="btn-finalizza-gironi"
              >
                ✅ Finalizza Gironi
              </button>
            )}
            {tournament.status === 'knockout' && tournament.config?.knockout?.enabled && (
              <button
                onClick={handleFinalizeTournament}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 font-medium"
                data-testid="btn-finalizza-torneo"
              >
                🏆 Chiudi Torneo
              </button>
            )}
            <button
              onClick={handleDeleteTournament}
              className="px-4 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 font-medium"
              data-testid="btn-elimina-torneo"
            >
              🗑️
            </button>
          </div>
        </div>
      </div>

      {/* Messages */}
      {msg.text && (
        <div className={`px-4 py-3 rounded-lg ${msg.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {msg.text}
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="border-b border-gray-200 flex overflow-x-auto">
          {(() => {
            // Determina se è un campionato: type === 'campionato' oppure format === 'campionato' oppure non ha knockout abilitato
            const isCampionato = tournament.type === 'campionato' || 
                                 tournament.format === 'campionato' || 
                                 (tournament.config?.knockout?.enabled === false && (tournament.groups?.length || 0) > 0);
            const hasKnockout = tournament.config?.knockout?.enabled !== false;
            
            return [
              { id: 'overview', label: 'Panoramica', icon: '📊' },
              { id: 'teams', label: 'Squadre', icon: '🏟️' },
              { id: 'groups', label: isCampionato ? 'Giornate' : 'Gironi', icon: '📋' },
              { id: 'knockout', label: 'Eliminazione', icon: '⚔️', hide: !hasKnockout },
              { id: 'ranking', label: 'Classifica', icon: '🏅' }
            ].filter(tab => !tab.hide);
          })().map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                px-6 py-4 font-medium whitespace-nowrap transition-colors
                ${activeTab === tab.id 
                  ? 'border-b-2 border-blue-500 text-blue-600 bg-blue-50' 
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}
              `}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <TournamentOverview tournament={tournament} />
          )}

          {/* Teams Management Tab */}
          {activeTab === 'teams' && (
            <TournamentTeams 
              tournament={tournament}
              onUpdate={loadTournament}
            />
          )}

          {/* Groups Tab */}
          {activeTab === 'groups' && (
            <TournamentGroups 
              tournament={tournament}
              onRecordResult={handleRecordGroupMatch}
              savingResult={savingResult}
              onUpdateGiornataDate={handleUpdateGiornataDate}
              selectedGroup={selectedGroupIndex}
              setSelectedGroup={setSelectedGroupIndex}
            />
          )}

          {/* Knockout Tab */}
          {activeTab === 'knockout' && (
            <TournamentKnockout 
              tournament={tournament}
              onRecordResult={handleRecordKnockoutMatch}
              onInitialize={handleInitializeKnockout}
              savingResult={savingResult}
            />
          )}

          {/* Ranking Tab */}
          {activeTab === 'ranking' && (
            <TournamentRanking tournament={tournament} />
          )}
        </div>
      </div>

      {/* Modal Conferma Avvio Torneo */}
      {showStartConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white p-6">
              <h3 className="text-xl font-bold">⚠️ Conferma Avvio Torneo</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                <p className="text-yellow-800 font-medium">
                  🔒 Attenzione: questa azione è irreversibile!
                </p>
                <p className="text-yellow-700 text-sm mt-2">
                  Una volta avviato il torneo:
                </p>
                <ul className="text-yellow-700 text-sm mt-1 list-disc ml-4 space-y-1">
                  <li>Non potrai più modificare nome, partecipanti o configurazione</li>
                  <li>I gironi e il calendario verranno generati automaticamente</li>
                  <li>Il torneo passerà in stato "Fase Gironi"</li>
                </ul>
              </div>
              <p className="text-gray-600">
                Sei sicuro di voler avviare il torneo <strong>"{tournament?.name}"</strong>?
              </p>
            </div>
            <div className="border-t border-gray-200 p-4 flex justify-end gap-3 bg-gray-50">
              <button
                onClick={() => setShowStartConfirm(false)}
                className="px-6 py-2 text-gray-600 hover:text-gray-800 font-medium"
                data-testid="btn-annulla-avvio"
              >
                Annulla
              </button>
              <button
                onClick={handleConfirmStart}
                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-medium"
                data-testid="btn-conferma-avvio"
              >
                🚀 Sì, Avvia Torneo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Modifica Torneo Bozza */}
      {showEditModal && (
        <EditTournamentModal
          editForm={editForm}
          setEditForm={setEditForm}
          onSave={handleSaveEdit}
          onCancel={() => setShowEditModal(false)}
          onAddParticipant={addParticipantToEdit}
          onRemoveParticipant={removeParticipantFromEdit}
          isSaving={isSaving}
          tournamentName={tournament?.name}
        />
      )}
    </div>
  );
};

// ============================================
// ✏️ EDIT TOURNAMENT MODAL COMPONENT
// ============================================

const EditTournamentModal = ({
  editForm,
  setEditForm,
  onSave,
  onCancel,
  onAddParticipant,
  onRemoveParticipant,
  isSaving,
  tournamentName
}) => {
  const [newName, setNewName] = useState('');
  const [newTeam, setNewTeam] = useState('');

  const handleAdd = () => {
    if (newName.trim()) {
      onAddParticipant(newName, newTeam);
      setNewName('');
      setNewTeam('');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-gray-600 to-gray-700 text-white p-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold">✏️ Modifica Torneo</h2>
            <button
              onClick={onCancel}
              className="text-white/80 hover:text-white text-2xl"
            >
              ✕
            </button>
          </div>
          <p className="text-white/70 text-sm mt-1">
            Modifica "{tournamentName}" prima di avviarlo
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Nome torneo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nome Torneo *
            </label>
            <input
              type="text"
              value={editForm.name}
              onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Nome del torneo"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              data-testid="edit-tournament-name"
            />
          </div>

          {/* Note */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Note (opzionale)
            </label>
            <textarea
              value={editForm.notes}
              onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Descrizione o note sul torneo..."
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              data-testid="edit-tournament-notes"
            />
          </div>

          {/* Partecipanti */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Partecipanti ({editForm.participants.length})
            </label>
            
            {/* Input nuovo partecipante */}
            <div className="bg-gray-50 p-4 rounded-xl mb-4 space-y-3">
              <div className="flex gap-3">
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAdd()}
                  placeholder="Nome giocatore *"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  data-testid="edit-participant-name"
                />
                <input
                  type="text"
                  value={newTeam}
                  onChange={(e) => setNewTeam(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAdd()}
                  placeholder="Squadra (opzionale)"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  data-testid="edit-participant-team"
                />
                <button
                  onClick={handleAdd}
                  disabled={!newName.trim()}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="edit-btn-add"
                >
                  ➕
                </button>
              </div>
            </div>

            {/* Lista partecipanti */}
            <div className="bg-white border border-gray-200 rounded-xl max-h-64 overflow-y-auto">
              {editForm.participants.length === 0 ? (
                <div className="p-8 text-center text-gray-400">
                  <span className="text-4xl">👥</span>
                  <p className="mt-2">Nessun partecipante</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {editForm.participants.map((p, index) => (
                    <div key={p.id} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50">
                      <div className="flex items-center gap-3">
                        <span className="w-7 h-7 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold text-sm">
                          {index + 1}
                        </span>
                        <div className="flex flex-col">
                          {p.team_name ? (
                            <>
                              <span className="font-bold text-gray-800">{p.team_name}</span>
                              <span className="text-sm text-gray-500">({p.name})</span>
                            </>
                          ) : (
                            <span className="font-medium">{p.name}</span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => onRemoveParticipant(p.id)}
                        className="text-red-500 hover:text-red-700 p-1 hover:bg-red-50 rounded"
                        data-testid={`edit-btn-remove-${p.id}`}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {editForm.participants.length < 2 && (
              <p className="mt-2 text-sm text-yellow-600">
                ⚠️ Servono almeno 2 partecipanti
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 flex justify-between items-center bg-gray-50">
          <button
            onClick={onCancel}
            className="px-6 py-2 text-gray-600 hover:text-gray-800 font-medium"
          >
            Annulla
          </button>
          <button
            onClick={onSave}
            disabled={isSaving || !editForm.name.trim() || editForm.participants.length < 2}
            className="px-8 py-2 bg-blue-500 text-white rounded-lg font-bold hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="edit-btn-save"
          >
            {isSaving ? '⏳ Salvataggio...' : '💾 Salva Modifiche'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// 🖼️ PARTICIPANT DISPLAY COMPONENT
// ============================================

const ParticipantLogo = ({ logoUrl, name, teamName, size = 'md', className = '' }) => {
  const sizeClasses = {
    sm: 'w-6 h-6 text-xs',
    md: 'w-8 h-8 text-sm',
    lg: 'w-10 h-10 text-base'
  };
  
  const initial = (teamName || name || '?')[0].toUpperCase();
  
  if (logoUrl) {
    return (
      <img 
        src={`${BACKEND_URL}${logoUrl}`}
        alt={teamName || name}
        className={`${sizeClasses[size]} rounded-full object-cover border border-gray-200 ${className}`}
      />
    );
  }
  
  return (
    <div className={`${sizeClasses[size]} rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-bold ${className}`}>
      {initial}
    </div>
  );
};

const ParticipantDisplay = ({ 
  name, 
  teamName, 
  logoUrl, 
  showLogo = true,
  align = 'left',
  size = 'md'
}) => {
  const displayName = teamName || name;
  const subText = teamName ? name : null;
  
  const alignClass = align === 'right' ? 'flex-row-reverse text-right' : 'flex-row text-left';
  const gapClass = align === 'right' ? 'gap-2' : 'gap-2';
  
  return (
    <div className={`flex items-center ${alignClass} ${gapClass}`}>
      {showLogo && (
        <ParticipantLogo 
          logoUrl={logoUrl} 
          name={name} 
          teamName={teamName} 
          size={size}
        />
      )}
      <div className={`flex flex-col ${align === 'right' ? 'items-end' : 'items-start'}`}>
        <span className="font-bold text-gray-800 leading-tight">{displayName}</span>
        {subText && (
          <span className="text-xs text-gray-500 leading-tight">({subText})</span>
        )}
      </div>
    </div>
  );
};

// ============================================
// 📊 OVERVIEW COMPONENT
// ============================================

const TournamentOverview = ({ tournament }) => {
  const config = tournament.config || {};
  const participants = tournament.participants || [];
  const groups = tournament.groups || [];

  const totalMatches = groups.reduce((sum, g) => sum + (g.matches?.length || 0), 0);
  const playedMatches = groups.reduce((sum, g) => 
    sum + (g.matches?.filter(m => m.played)?.length || 0), 0
  );

  return (
    <div className="space-y-6">
      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 p-5 rounded-xl text-center">
          <p className="text-3xl font-bold text-blue-600">{participants.length}</p>
          <p className="text-gray-600 text-sm">Partecipanti</p>
        </div>
        <div className="bg-green-50 p-5 rounded-xl text-center">
          <p className="text-3xl font-bold text-green-600">{groups.length}</p>
          <p className="text-gray-600 text-sm">Gironi</p>
        </div>
        <div className="bg-orange-50 p-5 rounded-xl text-center">
          <p className="text-3xl font-bold text-orange-600">{playedMatches}/{totalMatches}</p>
          <p className="text-gray-600 text-sm">Partite Giocate</p>
        </div>
        <div className="bg-purple-50 p-5 rounded-xl text-center">
          <p className="text-3xl font-bold text-purple-600">
            {tournament.qualified?.length || 0}
          </p>
          <p className="text-gray-600 text-sm">Qualificati</p>
        </div>
      </div>

      {/* Configuration */}
      <div className="bg-gray-50 p-5 rounded-xl">
        <h3 className="font-bold text-gray-800 mb-4">⚙️ Configurazione</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-gray-500">Tipo</p>
            <p className="font-medium">{config.type === 'campionato' ? 'Campionato' : 'Torneo'}</p>
          </div>
          <div>
            <p className="text-gray-500">Gironi</p>
            <p className="font-medium">{config.group_stage?.number_of_groups || 1}</p>
          </div>
          <div>
            <p className="text-gray-500">Andata/Ritorno</p>
            <p className="font-medium">{config.group_stage?.home_away ? 'Sì' : 'No'}</p>
          </div>
          <div>
            <p className="text-gray-500">Punti Vittoria</p>
            <p className="font-medium">{config.group_stage?.points_win || 3}</p>
          </div>
          <div>
            <p className="text-gray-500">Qualificazione</p>
            <p className="font-medium">
              {config.qualification?.method === 'direct' ? 'Diretta' : 'Trasversale'}
            </p>
          </div>
          <div>
            <p className="text-gray-500">Fase Finale</p>
            <p className="font-medium">{config.knockout?.enabled ? 'Sì' : 'No'}</p>
          </div>
        </div>
      </div>

      {/* Participants list */}
      <div className="bg-white border border-gray-200 rounded-xl">
        <div className="p-4 border-b border-gray-200">
          <h3 className="font-bold text-gray-800">👥 Partecipanti</h3>
        </div>
        <div className="p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {participants.map((p, i) => (
            <div 
              key={p.id || i}
              className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg"
            >
              <ParticipantLogo 
                logoUrl={p.logo_url} 
                name={p.name} 
                teamName={p.team_name} 
                size="sm"
              />
              <div className="flex flex-col min-w-0">
                <span className="font-medium text-gray-800 text-sm truncate">
                  {p.team_name || p.name}
                </span>
                {p.team_name && (
                  <span className="text-xs text-gray-500 truncate">({p.name})</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============================================
// 🏟️ TEAMS MANAGEMENT COMPONENT
// ============================================

const TournamentTeams = ({ tournament, onUpdate }) => {
  const [uploadingFor, setUploadingFor] = useState(null);
  const [editingTeam, setEditingTeam] = useState(null);
  const [editTeamName, setEditTeamName] = useState('');
  const [message, setMessage] = useState(null);
  
  const participants = tournament.participants || [];
  const token = localStorage.getItem('club_token');
  
  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };
  
  const handleLogoUpload = async (participantId, file) => {
    if (!file) return;
    
    setUploadingFor(participantId);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/clubs/tornei-v3/${tournament.id}/partecipanti/${participantId}/logo`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        }
      );
      
      if (response.ok) {
        showMessage('Logo caricato con successo!');
        onUpdate && onUpdate();
      } else {
        const err = await response.json();
        showMessage(err.detail || 'Errore upload', 'error');
      }
    } catch (error) {
      showMessage('Errore connessione', 'error');
    }
    
    setUploadingFor(null);
  };
  
  const handleDeleteLogo = async (participantId) => {
    if (!window.confirm('Rimuovere il logo?')) return;
    
    setUploadingFor(participantId);
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/clubs/tornei-v3/${tournament.id}/partecipanti/${participantId}/logo`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.ok) {
        showMessage('Logo rimosso');
        onUpdate && onUpdate();
      } else {
        showMessage('Errore rimozione', 'error');
      }
    } catch (error) {
      showMessage('Errore connessione', 'error');
    }
    
    setUploadingFor(null);
  };
  
  const handleUpdateTeam = async (participantId) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/clubs/tornei-v3/${tournament.id}/partecipanti/${participantId}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ team_name: editTeamName })
        }
      );
      
      if (response.ok) {
        showMessage('Nome squadra aggiornato!');
        setEditingTeam(null);
        setEditTeamName('');
        onUpdate && onUpdate();
      } else {
        showMessage('Errore aggiornamento', 'error');
      }
    } catch (error) {
      showMessage('Errore connessione', 'error');
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-xl font-bold text-gray-800">Gestione Squadre e Loghi</h3>
        <p className="text-gray-500 text-sm mt-1">
          Aggiungi loghi e modifica nomi squadra per i partecipanti
        </p>
      </div>
      
      {message && (
        <div className={`px-4 py-3 rounded-lg ${message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {message.text}
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {participants.map(p => (
          <div 
            key={p.id}
            className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center gap-4">
              {/* Logo area */}
              <div className="relative flex-shrink-0">
                {p.logo_url ? (
                  <div className="relative group">
                    <img 
                      src={`${BACKEND_URL}${p.logo_url}`}
                      alt={p.team_name || p.name}
                      className="w-16 h-16 rounded-full object-cover border-2 border-gray-200"
                    />
                    <button
                      onClick={() => handleDeleteLogo(p.id)}
                      className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                      disabled={uploadingFor === p.id}
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <label className="w-16 h-16 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors">
                    {uploadingFor === p.id ? (
                      <span className="animate-spin">⏳</span>
                    ) : (
                      <span className="text-2xl text-gray-400">📷</span>
                    )}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="hidden"
                      onChange={(e) => handleLogoUpload(p.id, e.target.files?.[0])}
                      disabled={uploadingFor === p.id}
                    />
                  </label>
                )}
              </div>
              
              {/* Info */}
              <div className="flex-1 min-w-0">
                {editingTeam === p.id ? (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={editTeamName}
                      onChange={(e) => setEditTeamName(e.target.value)}
                      placeholder="Nome squadra"
                      className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                      autoFocus
                    />
                    <button
                      onClick={() => handleUpdateTeam(p.id)}
                      className="px-2 py-1 bg-green-500 text-white rounded text-sm"
                    >
                      ✓
                    </button>
                    <button
                      onClick={() => setEditingTeam(null)}
                      className="px-2 py-1 bg-gray-300 text-gray-700 rounded text-sm"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <>
                    <p className="font-bold text-gray-800 truncate">
                      {p.team_name || <span className="text-gray-400 italic">Nessuna squadra</span>}
                    </p>
                    <p className="text-sm text-gray-500 truncate">{p.name}</p>
                    <button
                      onClick={() => {
                        setEditingTeam(p.id);
                        setEditTeamName(p.team_name || '');
                      }}
                      className="text-xs text-blue-500 hover:text-blue-700 mt-1"
                    >
                      ✏️ Modifica squadra
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      
      <div className="bg-blue-50 p-4 rounded-xl text-sm text-blue-700">
        <strong>💡 Suggerimento:</strong> Clicca sull'icona 📷 per caricare un logo. 
        I loghi vengono automaticamente ridimensionati e ritagliati in forma circolare (200x200px).
      </div>
    </div>
  );
};


// ============================================
// 📋 GROUPS COMPONENT
// ============================================

const TournamentGroups = ({ tournament, onRecordResult, savingResult, onUpdateGiornataDate, selectedGroup = 0, setSelectedGroup }) => {
  const groups = tournament.groups || [];
  const giornate_dates = tournament.giornate_dates || {};
  // Use props for selectedGroup if provided, otherwise use internal state as fallback
  const [internalSelectedGroup, setInternalSelectedGroup] = useState(0);
  const currentSelectedGroup = setSelectedGroup ? selectedGroup : internalSelectedGroup;
  const handleSetSelectedGroup = setSelectedGroup || setInternalSelectedGroup;
  
  const [editingMatch, setEditingMatch] = useState(null);
  const [scoreA, setScoreA] = useState(0);
  const [scoreB, setScoreB] = useState(0);
  
  // Determina se è un campionato
  const isCampionato = tournament.type === 'campionato' || 
                       tournament.format === 'campionato' || 
                       (tournament.config?.knockout?.enabled === false && groups.length > 0);
  
  // Default vista giornata per campionati con 1 solo girone
  const [viewByGiornata, setViewByGiornata] = useState(isCampionato && groups.length === 1);
  const [editingGiornataDate, setEditingGiornataDate] = useState(null);
  const [tempDate, setTempDate] = useState('');

  if (groups.length === 0) {
    return (
      <div className="text-center py-12">
        <span className="text-6xl">📋</span>
        <p className="mt-4 text-gray-600">I gironi non sono ancora stati generati.</p>
        <p className="text-gray-500 text-sm mt-2">Clicca "Avvia Torneo" per iniziare.</p>
      </div>
    );
  }

  const currentGroup = groups[currentSelectedGroup];

  // Raggruppa partite per giornata
  const matchesByGiornata = {};
  groups.forEach(group => {
    (group.matches || []).forEach(match => {
      const giornata = match.round_number || 1;
      if (!matchesByGiornata[giornata]) {
        matchesByGiornata[giornata] = [];
      }
      matchesByGiornata[giornata].push({ ...match, groupName: group.name, groupId: group.id });
    });
  });
  const giornateList = Object.keys(matchesByGiornata).map(Number).sort((a, b) => a - b);

  const handleSaveResult = () => {
    // Supporta sia vista girone (currentGroup) che vista giornata (editingMatch.groupId)
    const groupIdToUse = editingMatch?.groupId || currentGroup?.id;
    if (editingMatch && groupIdToUse) {
      onRecordResult(groupIdToUse, editingMatch.id, scoreA, scoreB);
      setEditingMatch(null);
      setScoreA(0);
      setScoreB(0);
    }
  };

  const openEditMatch = (match) => {
    setEditingMatch(match);
    setScoreA(match.score_a || 0);
    setScoreB(match.score_b || 0);
  };

  const handleSaveGiornataDate = (giornata) => {
    if (onUpdateGiornataDate) {
      onUpdateGiornataDate(giornata, tempDate || null);
    }
    setEditingGiornataDate(null);
    setTempDate('');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('it-IT', { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short' 
    });
  };

  return (
    <div className="space-y-6">
      {/* Toggle vista Gironi / Giornate */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2 overflow-x-auto pb-2">
          {!viewByGiornata && groups.map((group, index) => (
            <button
              key={group.id}
              onClick={() => handleSetSelectedGroup(index)}
              className={`
                px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors
                ${currentSelectedGroup === index 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}
              `}
            >
              Girone {group.name}
            </button>
          ))}
          {viewByGiornata && (
            <span className="px-4 py-2 text-gray-600 font-medium">
              📅 Calendario per Giornata
            </span>
          )}
        </div>
        
        {/* Toggle vista - nascosto se c'è un solo girone (per campionati) */}
        {groups.length > 1 && (
          <button
            onClick={() => setViewByGiornata(!viewByGiornata)}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
              viewByGiornata 
                ? 'bg-blue-500 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            data-testid="toggle-view-giornata"
          >
            {viewByGiornata ? '👥 Per Girone' : '📅 Per Giornata'}
          </button>
        )}
      </div>

      {/* Vista per Giornata */}
      {viewByGiornata ? (
        <div className="space-y-4">
          {giornateList.map(giornata => {
            const matches = matchesByGiornata[giornata];
            const played = matches.filter(m => m.played).length;
            const total = matches.length;
            const dateStr = giornate_dates[giornata];
            
            return (
              <div key={giornata} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                {/* Header Giornata */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 border-b border-gray-200 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl font-bold text-blue-600">{giornata}</span>
                    <div>
                      <h3 className="font-bold text-gray-800">Giornata {giornata}</h3>
                      <p className="text-sm text-gray-500">{played}/{total} partite giocate</p>
                    </div>
                  </div>
                  
                  {/* Data Giornata */}
                  <div className="flex items-center gap-2">
                    {editingGiornataDate === giornata ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="date"
                          value={tempDate}
                          onChange={(e) => setTempDate(e.target.value)}
                          className="px-3 py-1 border border-gray-300 rounded-lg text-sm"
                          data-testid={`input-date-giornata-${giornata}`}
                        />
                        <button
                          onClick={() => handleSaveGiornataDate(giornata)}
                          className="px-3 py-1 bg-green-500 text-white rounded-lg text-sm hover:bg-green-600"
                        >
                          ✓
                        </button>
                        <button
                          onClick={() => { setEditingGiornataDate(null); setTempDate(''); }}
                          className="px-3 py-1 bg-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-400"
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => { setEditingGiornataDate(giornata); setTempDate(dateStr || ''); }}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          dateStr 
                            ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' 
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                        data-testid={`btn-set-date-giornata-${giornata}`}
                      >
                        📅 {dateStr ? formatDate(dateStr) : 'Imposta data'}
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Partite della giornata */}
                <div className="divide-y divide-gray-100">
                  {matches.map(match => (
                    <div 
                      key={match.id}
                      className={`p-3 hover:bg-gray-50 ${match.played ? '' : 'bg-yellow-50'}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        {/* Girone badge */}
                        {groups.length > 1 && (
                          <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-600 rounded-full flex-shrink-0">
                            {match.groupName}
                          </span>
                        )}
                        
                        {/* Team A */}
                        <div className="flex-1 min-w-0">
                          <ParticipantDisplay 
                            name={match.team_a_name}
                            teamName={match.team_a_team}
                            logoUrl={match.team_a_logo}
                            align="left"
                            size="sm"
                          />
                        </div>
                        
                        {/* Score */}
                        <div className="flex-shrink-0">
                          {match.played ? (
                            <span className="px-3 py-1 bg-gray-100 rounded font-bold">
                              {match.score_a} - {match.score_b}
                            </span>
                          ) : (
                            <span className="px-3 py-1 bg-yellow-100 rounded text-yellow-700 font-medium">
                              vs
                            </span>
                          )}
                        </div>
                        
                        {/* Team B */}
                        <div className="flex-1 min-w-0">
                          <ParticipantDisplay 
                            name={match.team_b_name}
                            teamName={match.team_b_team}
                            logoUrl={match.team_b_logo}
                            align="right"
                            size="sm"
                          />
                        </div>
                        
                        {/* Edit button */}
                        <button
                          onClick={() => openEditMatch(match)}
                          className={`flex-shrink-0 px-2 py-1 rounded text-sm ${
                            match.played 
                              ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' 
                              : 'bg-blue-500 text-white hover:bg-blue-600'
                          }`}
                          data-testid={`edit-match-giornata-${match.id}`}
                        >
                          ✏️
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* Vista per Girone - Solo partite (classifica nel tab Classifica) */
        currentGroup && (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="bg-gray-50 p-4 border-b border-gray-200">
              <h3 className="font-bold text-gray-800">⚽ Partite - Girone {currentGroup.name}</h3>
            </div>
            <div className="divide-y divide-gray-100">
              {(currentGroup.matches || []).map(match => (
                <div 
                  key={match.id}
                  className={`p-3 hover:bg-gray-50 ${match.played ? '' : 'bg-yellow-50'}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    {/* Team A */}
                    <div className="flex-1 min-w-0">
                      <ParticipantDisplay 
                        name={match.team_a_name}
                        teamName={match.team_a_team}
                        logoUrl={match.team_a_logo}
                        align="left"
                        size="sm"
                      />
                    </div>
                    
                    {/* Score */}
                    <div className="flex-shrink-0">
                      {match.played ? (
                        <span className="px-3 py-1 bg-gray-100 rounded font-bold text-lg">
                          {match.score_a} - {match.score_b}
                        </span>
                      ) : (
                        <span className="px-3 py-1 bg-yellow-100 rounded text-yellow-700 font-medium">
                          vs
                        </span>
                      )}
                    </div>
                    
                    {/* Team B */}
                    <div className="flex-1 min-w-0">
                      <ParticipantDisplay 
                        name={match.team_b_name}
                        teamName={match.team_b_team}
                        logoUrl={match.team_b_logo}
                        align="right"
                        size="sm"
                      />
                    </div>
                    
                    {/* Edit button */}
                    <button
                      onClick={() => openEditMatch(match)}
                      className={`flex-shrink-0 px-2 py-1 rounded text-sm ${
                        match.played 
                          ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' 
                          : 'bg-blue-500 text-white hover:bg-blue-600'
                      }`}
                    >
                      ✏️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      )}

      {/* Edit Match Modal */}
      {editingMatch && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-6 text-center">
              Inserisci Risultato
            </h3>
            
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="text-center flex-1">
                <div className="flex flex-col items-center gap-1 mb-2">
                  <ParticipantLogo 
                    logoUrl={editingMatch.team_a_logo} 
                    name={editingMatch.team_a_name}
                    teamName={editingMatch.team_a_team}
                    size="md"
                  />
                  <p className="font-bold text-gray-800">
                    {editingMatch.team_a_team || editingMatch.team_a_name}
                  </p>
                  {editingMatch.team_a_team && (
                    <p className="text-xs text-gray-500">({editingMatch.team_a_name})</p>
                  )}
                </div>
                <input
                  type="number"
                  min="0"
                  value={scoreA}
                  onChange={(e) => setScoreA(parseInt(e.target.value) || 0)}
                  className="w-20 px-4 py-3 border-2 border-gray-300 rounded-lg text-center text-2xl font-bold focus:border-blue-500"
                  data-testid="input-score-a"
                />
              </div>
              <span className="text-2xl text-gray-400">-</span>
              <div className="text-center flex-1">
                <div className="flex flex-col items-center gap-1 mb-2">
                  <ParticipantLogo 
                    logoUrl={editingMatch.team_b_logo} 
                    name={editingMatch.team_b_name}
                    teamName={editingMatch.team_b_team}
                    size="md"
                  />
                  <p className="font-bold text-gray-800">
                    {editingMatch.team_b_team || editingMatch.team_b_name}
                  </p>
                  {editingMatch.team_b_team && (
                    <p className="text-xs text-gray-500">({editingMatch.team_b_name})</p>
                  )}
                </div>
                <input
                  type="number"
                  min="0"
                  value={scoreB}
                  onChange={(e) => setScoreB(parseInt(e.target.value) || 0)}
                  className="w-20 px-4 py-3 border-2 border-gray-300 rounded-lg text-center text-2xl font-bold focus:border-blue-500"
                  data-testid="input-score-b"
                />
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setEditingMatch(null)}
                className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200"
              >
                Annulla
              </button>
              <button
                onClick={handleSaveResult}
                disabled={savingResult === editingMatch.id}
                className="flex-1 py-3 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 disabled:opacity-50"
                data-testid="btn-save-result"
              >
                {savingResult === editingMatch.id ? 'Salvataggio...' : '✅ Salva'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// ⚔️ KNOCKOUT COMPONENT
// ============================================

const TournamentKnockout = ({ tournament, onRecordResult, onInitialize, savingResult }) => {
  const brackets = tournament.brackets || {};
  const qualified = tournament.qualified || [];
  const [editingMatch, setEditingMatch] = useState(null);
  const [scoreA, setScoreA] = useState(0);
  const [scoreB, setScoreB] = useState(0);
  const [scoreALeg2, setScoreALeg2] = useState(0);
  const [scoreBLeg2, setScoreBLeg2] = useState(0);
  const [penA, setPenA] = useState(null);
  const [penB, setPenB] = useState(null);
  const [currentBracket, setCurrentBracket] = useState(null);
  const [currentLeg, setCurrentLeg] = useState(1); // 1 = andata, 2 = ritorno
  const [viewMode, setViewMode] = useState('bracket'); // 'bracket' | 'list'

  const bracketKeys = Object.keys(brackets);

  if (tournament.status === 'draft') {
    return (
      <div className="text-center py-12">
        <span className="text-6xl">⚔️</span>
        <p className="mt-4 text-gray-600">Prima devi avviare la fase gironi.</p>
      </div>
    );
  }

  if (tournament.status === 'groups') {
    if (qualified.length === 0) {
      return (
        <div className="text-center py-12">
          <span className="text-6xl">⏳</span>
          <p className="mt-4 text-gray-600">Completa i gironi per passare alla fase eliminazione.</p>
          <p className="text-gray-500 text-sm mt-2">Clicca "Finalizza Gironi" quando tutte le partite sono complete.</p>
        </div>
      );
    }

    return (
      <div className="text-center py-12">
        <span className="text-6xl">🎯</span>
        <p className="mt-4 text-gray-600">{qualified.length} giocatori qualificati!</p>
        <button
          onClick={onInitialize}
          className="mt-4 px-6 py-3 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600"
        >
          🚀 Genera Tabellone
        </button>
        
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          {qualified.map(q => (
            <span key={q.participant_id} className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
              {q.name} ({q.group_name})
            </span>
          ))}
        </div>
      </div>
    );
  }

  if (bracketKeys.length === 0) {
    return (
      <div className="text-center py-12">
        <span className="text-6xl">📊</span>
        <p className="mt-4 text-gray-600">Nessun tabellone generato.</p>
      </div>
    );
  }

  const openEditMatch = (bracketKey, match, leg = 1) => {
    setCurrentBracket(bracketKey);
    setEditingMatch(match);
    setCurrentLeg(leg);
    
    if (match.is_home_away) {
      if (leg === 1) {
        // Andata - usa score_a e score_b
        setScoreA(match.score_a || 0);
        setScoreB(match.score_b || 0);
      } else {
        // Ritorno - usa score_a_leg2 e score_b_leg2
        setScoreALeg2(match.score_a_leg2 || 0);
        setScoreBLeg2(match.score_b_leg2 || 0);
      }
    } else {
      setScoreA(match.score_a || 0);
      setScoreB(match.score_b || 0);
    }
    
    setPenA(match.penalties_a || null);
    setPenB(match.penalties_b || null);
  };

  const handleSaveResult = () => {
    if (editingMatch && currentBracket) {
      // Check if penalties are required (draw situation)
      const isDrawSingleMatch = !editingMatch.is_home_away && scoreA === scoreB;
      const isDrawHomeAway = editingMatch.is_home_away && currentLeg === 2 && 
        ((editingMatch.score_a || 0) + scoreALeg2) === ((editingMatch.score_b || 0) + scoreBLeg2);
      
      if (isDrawSingleMatch || isDrawHomeAway) {
        // Penalties required - validate they are entered and different
        if (penA === null || penB === null) {
          alert('⚠️ Parità! Inserisci il risultato dei rigori per determinare il vincitore.');
          return;
        }
        if (penA === penB) {
          alert('⚠️ I rigori non possono essere in parità. Inserisci un risultato valido.');
          return;
        }
      }
      
      if (editingMatch.is_home_away) {
        if (currentLeg === 1) {
          // Salva andata
          onRecordResult(currentBracket, editingMatch.id, scoreA, scoreB, null, null, 1);
        } else {
          // Salva ritorno (con eventuali rigori)
          onRecordResult(currentBracket, editingMatch.id, scoreALeg2, scoreBLeg2, penA, penB, 2);
        }
      } else {
        // Partita secca
        onRecordResult(currentBracket, editingMatch.id, scoreA, scoreB, penA, penB, 1);
      }
      setEditingMatch(null);
      setCurrentBracket(null);
    }
  };

  const getRoundName = (round) => {
    const names = {
      'final': 'Finale',
      'semifinal': 'Semifinale',
      'quarterfinal': 'Quarti',
      'round_of_16': 'Ottavi',
      'round_of_32': 'Sedicesimi',
      'third_place': '3°/4° Posto'
    };
    return names[round] || round;
  };

  return (
    <div className="space-y-6">
      {/* View Mode Toggle */}
      <div className="flex items-center justify-between bg-white border border-gray-200 rounded-xl p-4">
        <h4 className="text-sm font-medium text-gray-600">⚔️ Fase Eliminazione</h4>
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('bracket')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              viewMode === 'bracket' 
                ? 'bg-blue-500 text-white shadow-md' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            data-testid="view-mode-bracket"
          >
            🏆 Tabellone
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              viewMode === 'list' 
                ? 'bg-blue-500 text-white shadow-md' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            data-testid="view-mode-list"
          >
            📋 Lista
          </button>
        </div>
      </div>

      {/* BRACKET VIEW - Tabellone Grafico */}
      {viewMode === 'bracket' && (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <MultiBracketTabs
            brackets={brackets}
            knockoutConfig={tournament.config?.knockout || {}}
            tournamentName={tournament.name}
            onMatchClick={(match) => {
              // Find bracket key for this match
              const bracketKey = bracketKeys.find(key => 
                brackets[key].matches?.some(m => m.id === match.id)
              );
              if (bracketKey && !match.played) {
                openEditMatch(bracketKey, match, 1);
              }
            }}
          />
        </div>
      )}

      {/* LIST VIEW - Vista Lista Originale */}
      {viewMode === 'list' && (
        <>
      {/* Bracket selector with fascia colors */}
      {bracketKeys.length > 1 && (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <h4 className="text-sm font-medium text-gray-600 mb-3">🥇 Fasce Eliminazione</h4>
          <div className="flex flex-wrap gap-2">
            {bracketKeys.map(key => {
              const fasciaConfig = tournament.config?.fasce?.config?.find(
                f => f.name.toLowerCase() === key.toLowerCase() || key.toLowerCase().includes(f.name.toLowerCase())
              );
              const bgColor = fasciaConfig?.color === 'yellow' ? 'bg-yellow-100 text-yellow-800 border-yellow-400'
                : fasciaConfig?.color === 'gray' ? 'bg-gray-200 text-gray-700 border-gray-400'
                : fasciaConfig?.color === 'amber' ? 'bg-amber-100 text-amber-800 border-amber-500'
                : fasciaConfig?.color === 'blue' ? 'bg-blue-100 text-blue-800 border-blue-400'
                : fasciaConfig?.color === 'green' ? 'bg-green-100 text-green-800 border-green-400'
                : fasciaConfig?.color === 'purple' ? 'bg-purple-100 text-purple-800 border-purple-400'
                : 'bg-gray-100 text-gray-700 border-gray-300';
              
              const bracket = brackets[key];
              const totalMatches = bracket.matches?.filter(m => !m.team_a_id?.includes?.('BYE') && !m.team_b_id?.includes?.('BYE')).length || 0;
              const playedMatches = bracket.matches?.filter(m => m.played).length || 0;
              
              return (
                <div
                  key={key}
                  className={`px-4 py-2 rounded-lg border-2 ${bgColor} flex items-center gap-2`}
                >
                  <span className="font-bold capitalize">{key}</span>
                  <span className="text-xs opacity-75">({playedMatches}/{totalMatches})</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Bracket display */}
      {bracketKeys.map(bracketKey => {
        const bracket = brackets[bracketKey];
        const matches = bracket.matches || [];
        
        // Raggruppa per round
        const roundsMap = {};
        matches.forEach(m => {
          if (!roundsMap[m.round]) roundsMap[m.round] = [];
          roundsMap[m.round].push(m);
        });

        return (
          <div key={bracketKey} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className={`p-4 border-b border-gray-200 ${
              bracketKey.toLowerCase().includes('oro') || brackets[bracketKey]?.name?.toLowerCase()?.includes('oro') 
                ? 'bg-gradient-to-r from-yellow-50 to-yellow-100' 
                : bracketKey.toLowerCase().includes('argento') || brackets[bracketKey]?.name?.toLowerCase()?.includes('argento')
                  ? 'bg-gradient-to-r from-gray-100 to-gray-200'
                  : bracketKey.toLowerCase().includes('bronzo') || brackets[bracketKey]?.name?.toLowerCase()?.includes('bronzo')
                    ? 'bg-gradient-to-r from-amber-50 to-amber-100'
                    : 'bg-gray-50'
            }`}>
              <h3 className="font-bold text-gray-800 flex items-center gap-2">
                {bracketKey.toLowerCase().includes('oro') ? '🥇' 
                  : bracketKey.toLowerCase().includes('argento') ? '🥈' 
                  : bracketKey.toLowerCase().includes('bronzo') ? '🥉' 
                  : '🏆'}
                <span className="capitalize">{bracket.name || bracketKey}</span>
                <span className="text-sm font-normal text-gray-500">
                  ({bracket.size || matches.length} partecipanti)
                </span>
              </h3>
            </div>
            
            <div className="p-4 space-y-6">
              {Object.entries(roundsMap).map(([round, roundMatches]) => (
                <div key={round}>
                  <h4 className="font-medium text-gray-600 mb-3">{getRoundName(round)}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {roundMatches.map(match => {
                      // Calcola stato partita A/R
                      const hasLeg1 = match.score_a !== null && match.score_a !== undefined;
                      const hasLeg2 = match.score_a_leg2 !== null && match.score_a_leg2 !== undefined;
                      const isHomeAway = match.is_home_away;
                      const totalA = (match.score_a || 0) + (match.score_a_leg2 || 0);
                      const totalB = (match.score_b || 0) + (match.score_b_leg2 || 0);
                      
                      return (
                      <div 
                        key={match.id}
                        className={`p-4 border rounded-xl ${
                          match.played 
                            ? 'border-green-200 bg-green-50' 
                            : isHomeAway && hasLeg1 
                              ? 'border-blue-200 bg-blue-50'
                              : 'border-gray-200'
                        }`}
                      >
                        {/* Badge A/R */}
                        {isHomeAway && (
                          <div className="mb-2 flex items-center gap-2">
                            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-medium">
                              🔄 Andata/Ritorno
                            </span>
                            {hasLeg1 && !hasLeg2 && (
                              <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full">
                                Andata giocata
                              </span>
                            )}
                            {match.played && (
                              <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">
                                Completata
                              </span>
                            )}
                          </div>
                        )}
                        
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="font-medium">{match.team_a_name || 'TBD'}</span>
                              {isHomeAway && hasLeg1 ? (
                                <div className="flex items-center gap-1">
                                  <span className="font-bold text-lg">{match.score_a}</span>
                                  {hasLeg2 && (
                                    <>
                                      <span className="text-gray-400">+</span>
                                      <span className="font-bold text-lg">{match.score_a_leg2}</span>
                                      <span className="text-xs text-gray-500 ml-1">= {totalA}</span>
                                    </>
                                  )}
                                </div>
                              ) : match.played && (
                                <span className="font-bold text-lg">{match.score_a}</span>
                              )}
                              {match.winner_id === match.team_a_id && (
                                <span className="text-green-500">✓</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{match.team_b_name || 'TBD'}</span>
                              {isHomeAway && hasLeg1 ? (
                                <div className="flex items-center gap-1">
                                  <span className="font-bold text-lg">{match.score_b}</span>
                                  {hasLeg2 && (
                                    <>
                                      <span className="text-gray-400">+</span>
                                      <span className="font-bold text-lg">{match.score_b_leg2}</span>
                                      <span className="text-xs text-gray-500 ml-1">= {totalB}</span>
                                    </>
                                  )}
                                </div>
                              ) : match.played && (
                                <span className="font-bold text-lg">{match.score_b}</span>
                              )}
                              {match.winner_id === match.team_b_id && (
                                <span className="text-green-500">✓</span>
                              )}
                            </div>
                            {match.has_penalties && (
                              <p className="text-xs text-orange-600 mt-1 font-medium">
                                ⚽ Rigori: {match.penalties_a} - {match.penalties_b}
                              </p>
                            )}
                          </div>
                          
                          {/* Pulsanti per partite */}
                          {match.team_a_id && match.team_b_id && !match.played && (
                            <div className="flex flex-col gap-1">
                              {isHomeAway ? (
                                <>
                                  {!hasLeg1 && (
                                    <button
                                      onClick={() => openEditMatch(bracketKey, match, 1)}
                                      className="px-3 py-2 bg-blue-500 text-white rounded-lg text-xs hover:bg-blue-600"
                                      data-testid={`btn-leg1-${match.id}`}
                                    >
                                      📝 Andata
                                    </button>
                                  )}
                                  {hasLeg1 && !hasLeg2 && (
                                    <button
                                      onClick={() => openEditMatch(bracketKey, match, 2)}
                                      className="px-3 py-2 bg-orange-500 text-white rounded-lg text-xs hover:bg-orange-600"
                                      data-testid={`btn-leg2-${match.id}`}
                                    >
                                      📝 Ritorno
                                    </button>
                                  )}
                                  {hasLeg1 && hasLeg2 && totalA === totalB && !match.has_penalties && (
                                    <button
                                      onClick={() => openEditMatch(bracketKey, match, 2)}
                                      className="px-3 py-2 bg-red-500 text-white rounded-lg text-xs hover:bg-red-600"
                                      data-testid={`btn-penalties-${match.id}`}
                                    >
                                      ⚽ Rigori
                                    </button>
                                  )}
                                </>
                              ) : (
                                <button
                                  onClick={() => openEditMatch(bracketKey, match, 1)}
                                  className="px-3 py-2 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600"
                                >
                                  ✏️
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )})}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
        </>
      )}

      {/* Edit Match Modal */}
      {editingMatch && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            {/* Header con info A/R */}
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold text-gray-800">
                {editingMatch.round_name || getRoundName(editingMatch.round)}
              </h3>
              {editingMatch.is_home_away && (
                <span className={`
                  inline-block mt-2 px-3 py-1 rounded-full text-sm font-medium
                  ${currentLeg === 1 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'bg-orange-100 text-orange-700'}
                `}>
                  {currentLeg === 1 ? '📅 Partita di Andata' : '📅 Partita di Ritorno'}
                </span>
              )}
            </div>
            
            {/* Riepilogo Andata (se stiamo inserendo ritorno) */}
            {editingMatch.is_home_away && currentLeg === 2 && editingMatch.score_a !== null && (
              <div className="bg-gray-50 p-3 rounded-lg mb-4">
                <p className="text-xs text-gray-500 mb-1">Risultato Andata:</p>
                <div className="flex items-center justify-center gap-4">
                  <span className="font-medium">{editingMatch.team_a_name}</span>
                  <span className="font-bold text-lg">{editingMatch.score_a} - {editingMatch.score_b}</span>
                  <span className="font-medium">{editingMatch.team_b_name}</span>
                </div>
              </div>
            )}
            
            {/* Input risultato */}
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="text-center flex-1">
                <p className="font-medium text-gray-800 mb-2">{editingMatch.team_a_name}</p>
                <input
                  type="number"
                  min="0"
                  value={editingMatch.is_home_away && currentLeg === 2 ? scoreALeg2 : scoreA}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 0;
                    if (editingMatch.is_home_away && currentLeg === 2) {
                      setScoreALeg2(val);
                    } else {
                      setScoreA(val);
                    }
                  }}
                  className={`
                    w-20 px-4 py-3 border-2 rounded-lg text-center text-2xl font-bold
                    ${editingMatch.is_home_away && currentLeg === 2 
                      ? 'border-orange-300 focus:border-orange-500' 
                      : 'border-gray-300 focus:border-blue-500'}
                  `}
                  data-testid="input-score-a"
                />
              </div>
              <span className="text-2xl text-gray-400">-</span>
              <div className="text-center flex-1">
                <p className="font-medium text-gray-800 mb-2">{editingMatch.team_b_name}</p>
                <input
                  type="number"
                  min="0"
                  value={editingMatch.is_home_away && currentLeg === 2 ? scoreBLeg2 : scoreB}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 0;
                    if (editingMatch.is_home_away && currentLeg === 2) {
                      setScoreBLeg2(val);
                    } else {
                      setScoreB(val);
                    }
                  }}
                  className={`
                    w-20 px-4 py-3 border-2 rounded-lg text-center text-2xl font-bold
                    ${editingMatch.is_home_away && currentLeg === 2 
                      ? 'border-orange-300 focus:border-orange-500' 
                      : 'border-gray-300 focus:border-blue-500'}
                  `}
                  data-testid="input-score-b"
                />
              </div>
            </div>

            {/* Totale dopo ritorno (preview) */}
            {editingMatch.is_home_away && currentLeg === 2 && editingMatch.score_a !== null && (
              <div className="bg-blue-50 p-3 rounded-lg mb-4">
                <p className="text-xs text-blue-600 mb-1">Somma Totale (Andata + Ritorno):</p>
                <div className="flex items-center justify-center gap-4 font-bold text-lg">
                  <span className="text-blue-800">{(editingMatch.score_a || 0) + scoreALeg2}</span>
                  <span className="text-gray-400">-</span>
                  <span className="text-blue-800">{(editingMatch.score_b || 0) + scoreBLeg2}</span>
                </div>
                {((editingMatch.score_a || 0) + scoreALeg2) === ((editingMatch.score_b || 0) + scoreBLeg2) && (
                  <p className="text-xs text-orange-600 mt-2">⚠️ Parità! Inserisci il risultato dei rigori sotto.</p>
                )}
              </div>
            )}

            {/* Rigori (solo se pareggio dopo due gare A/R, o pareggio in partita secca) */}
            {((!editingMatch.is_home_away && scoreA === scoreB) || 
              (editingMatch.is_home_away && currentLeg === 2 && 
               ((editingMatch.score_a || 0) + scoreALeg2) === ((editingMatch.score_b || 0) + scoreBLeg2))) && (
              <div className="mb-6 bg-orange-50 p-4 rounded-lg border border-orange-200">
                <p className="text-center text-orange-700 font-medium mb-3">⚽ Rigori (pareggio totale)</p>
                <div className="flex items-center justify-center gap-4">
                  <input
                    type="number"
                    min="0"
                    value={penA || ''}
                    onChange={(e) => setPenA(e.target.value ? parseInt(e.target.value) : null)}
                    placeholder="0"
                    className="w-16 px-3 py-2 border border-orange-300 rounded-lg text-center font-bold"
                    data-testid="input-pen-a"
                  />
                  <span className="text-gray-400">-</span>
                  <input
                    type="number"
                    min="0"
                    value={penB || ''}
                    onChange={(e) => setPenB(e.target.value ? parseInt(e.target.value) : null)}
                    placeholder="0"
                    className="w-16 px-3 py-2 border border-orange-300 rounded-lg text-center font-bold"
                    data-testid="input-pen-b"
                  />
                </div>
              </div>
            )}
            
            <div className="flex gap-3">
              <button
                onClick={() => { setEditingMatch(null); setCurrentBracket(null); }}
                className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200"
                data-testid="btn-cancel-result"
              >
                Annulla
              </button>
              <button
                onClick={handleSaveResult}
                disabled={savingResult === editingMatch.id}
                className={`
                  flex-1 py-3 text-white rounded-lg font-medium disabled:opacity-50
                  ${editingMatch.is_home_away && currentLeg === 2 
                    ? 'bg-orange-500 hover:bg-orange-600' 
                    : 'bg-green-500 hover:bg-green-600'}
                `}
                data-testid="btn-save-result"
              >
                {savingResult === editingMatch.id 
                  ? 'Salvataggio...' 
                  : editingMatch.is_home_away 
                    ? (currentLeg === 1 ? '✅ Salva Andata' : '✅ Salva Ritorno')
                    : '✅ Salva'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// 🏅 RANKING COMPONENT
// ============================================

const TournamentRanking = ({ tournament }) => {
  const finalRanking = tournament.final_ranking || [];
  const qualified = tournament.qualified || [];
  const qualifiedByFascia = tournament.qualified_by_fascia || {};
  const qualificationConfig = tournament.config?.qualification || {};
  const fasceConfig = tournament.config?.fasce || {};
  const isTransversal = qualificationConfig.method === 'transversal';
  const isFasce = qualificationConfig.method === 'fasce';

  // Helper per label metodo qualificazione
  const getQualificationLabel = (method) => {
    if (!method) return '';
    if (method === 'direct') return '🏆 Diretto';
    if (method.startsWith('transversal_')) {
      const cat = method.replace('transversal_', '');
      const labels = {
        'second': '📊 Miglior Secondo',
        'third': '📊 Miglior Terzo',
        'fourth': '📊 Miglior Quarto'
      };
      return labels[cat] || `📊 Trasversale (${cat})`;
    }
    if (method.startsWith('direct_')) {
      return '🏆 Diretto';
    }
    return method;
  };

  // Helper per colore badge metodo
  const getMethodBadgeColor = (method) => {
    if (!method) return 'bg-gray-100 text-gray-600';
    if (method === 'direct' || method.startsWith('direct_')) return 'bg-green-100 text-green-700';
    if (method.startsWith('transversal_')) return 'bg-blue-100 text-blue-700';
    return 'bg-gray-100 text-gray-600';
  };

  // Helper per colore fascia
  const getFasciaColorClasses = (fasciaName) => {
    const fasciaConf = fasceConfig.config?.find(f => f.name.toLowerCase() === fasciaName.toLowerCase());
    const color = fasciaConf?.color || 'gray';
    const colors = {
      'yellow': { bg: 'bg-yellow-50', border: 'border-yellow-300', text: 'text-yellow-800', header: 'bg-gradient-to-r from-yellow-100 to-yellow-200' },
      'gray': { bg: 'bg-gray-50', border: 'border-gray-300', text: 'text-gray-700', header: 'bg-gradient-to-r from-gray-100 to-gray-200' },
      'amber': { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-800', header: 'bg-gradient-to-r from-amber-100 to-amber-200' },
      'blue': { bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-800', header: 'bg-gradient-to-r from-blue-100 to-blue-200' },
      'green': { bg: 'bg-green-50', border: 'border-green-300', text: 'text-green-800', header: 'bg-gradient-to-r from-green-100 to-green-200' },
      'purple': { bg: 'bg-purple-50', border: 'border-purple-300', text: 'text-purple-800', header: 'bg-gradient-to-r from-purple-100 to-purple-200' }
    };
    return colors[color] || colors.gray;
  };

  // Classifica finale con fasce
  if (tournament.status === 'completed' && finalRanking.length > 0) {
    // Raggruppa per bracket/fascia
    const rankingByBracket = {};
    finalRanking.forEach(entry => {
      const bracket = entry.bracket || 'main';
      if (!rankingByBracket[bracket]) rankingByBracket[bracket] = [];
      rankingByBracket[bracket].push(entry);
    });

    const hasFasce = Object.keys(rankingByBracket).length > 1 || 
      (Object.keys(rankingByBracket).length === 1 && !rankingByBracket['main']);

    return (
      <div className="space-y-6">
        <div className="text-center">
          <span className="text-6xl">🏆</span>
          <h3 className="text-2xl font-bold text-gray-800 mt-4">Classifica Finale</h3>
          {hasFasce && (
            <p className="text-gray-500 mt-2">
              Vincitori per ogni fascia del torneo
            </p>
          )}
        </div>

        {/* Mostra vincitori di ogni fascia */}
        {hasFasce ? (
          <div className="space-y-6">
            {Object.entries(rankingByBracket).map(([bracket, entries]) => {
              const colorClasses = getFasciaColorClasses(bracket);
              const winner = entries[0];
              const icon = bracket.toLowerCase().includes('oro') ? '🥇' 
                : bracket.toLowerCase().includes('argento') ? '🥈' 
                : bracket.toLowerCase().includes('bronzo') ? '🥉' 
                : '🏆';

              return (
                <div key={bracket} className={`${colorClasses.bg} border-2 ${colorClasses.border} rounded-xl overflow-hidden`}>
                  <div className={`${colorClasses.header} p-4 border-b ${colorClasses.border}`}>
                    <h4 className={`font-bold ${colorClasses.text} flex items-center gap-2`}>
                      <span className="text-2xl">{icon}</span>
                      <span className="capitalize text-xl">{bracket}</span>
                    </h4>
                  </div>
                  
                  {/* Vincitore */}
                  {winner && (
                    <div className="p-6 text-center bg-white/50">
                      <span className="text-4xl">{icon}</span>
                      <p className="text-2xl font-bold mt-2">{winner.team_name || winner.name}</p>
                      {winner.team_name && winner.name !== winner.team_name && (
                        <p className="text-gray-500">({winner.name})</p>
                      )}
                      <p className={`mt-2 ${colorClasses.text} font-medium`}>
                        Campione {bracket}
                      </p>
                    </div>
                  )}
                  
                  {/* Classifica completa fascia */}
                  <table className="w-full">
                    <thead className={`${colorClasses.bg}`}>
                      <tr className={`border-t ${colorClasses.border}`}>
                        <th className="px-4 py-2 text-left text-sm">Pos</th>
                        <th className="px-4 py-2 text-left text-sm">Giocatore</th>
                        <th className="px-4 py-2 text-center text-sm">Punti</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map((entry, i) => (
                        <tr 
                          key={entry.participant_id}
                          className={`border-t ${colorClasses.border} ${i === 0 ? 'bg-white/80' : ''}`}
                        >
                          <td className="px-4 py-3">
                            <span className={`${i < 3 ? 'text-xl' : ''}`}>
                              {i === 0 ? icon : i === 1 ? '🥈' : i === 2 ? '🥉' : i + 1}
                            </span>
                          </td>
                          <td className="px-4 py-3 font-medium">{entry.team_name || entry.name}</td>
                          <td className="px-4 py-3 text-center font-bold text-blue-600">
                            {entry.ranking_points || 0}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>
        ) : (
          /* Classifica standard senza fasce */
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left">Pos</th>
                  <th className="px-6 py-4 text-left">Giocatore</th>
                  <th className="px-6 py-4 text-center">Punti Ranking</th>
                </tr>
              </thead>
              <tbody>
                {finalRanking.map((entry, i) => (
                  <tr 
                    key={entry.participant_id}
                    className={`
                      ${i === 0 ? 'bg-yellow-50' : ''}
                      ${i === 1 ? 'bg-gray-100' : ''}
                      ${i === 2 ? 'bg-orange-50' : ''}
                    `}
                  >
                    <td className="px-6 py-4">
                      <span className="text-2xl">
                        {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : i + 1}
                      </span>
                    </td>
                    <td className="px-6 py-4 font-medium">{entry.name}</td>
                    <td className="px-6 py-4 text-center font-bold text-blue-600">
                      {entry.ranking_points || 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  // Mostra qualificati per fascia
  if (isFasce && Object.keys(qualifiedByFascia).length > 0) {
    return (
      <div className="space-y-8">
        <div className="text-center">
          <span className="text-6xl">🥇</span>
          <h3 className="text-2xl font-bold text-gray-800 mt-4">Qualificati per Fascia</h3>
          <p className="text-gray-500 mt-2">
            {qualified.length} giocatori totali qualificati in {Object.keys(qualifiedByFascia).length} fasce
          </p>
        </div>

        {/* Info sistema fasce */}
        <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200 rounded-xl p-4">
          <h4 className="font-bold text-yellow-800 mb-2">🥇 Sistema Fasce</h4>
          <p className="text-yellow-700 text-sm">
            I giocatori sono divisi in fasce in base al piazzamento nel proprio girone.
            Ogni fascia avrà il suo bracket eliminatorio separato con il proprio vincitore.
          </p>
        </div>

        {/* Fasce configurate */}
        {Object.entries(qualifiedByFascia).map(([fasciaName, fasciaQualified]) => {
          const colorClasses = getFasciaColorClasses(fasciaName);
          const icon = fasciaName.toLowerCase().includes('oro') ? '🥇' 
            : fasciaName.toLowerCase().includes('argento') ? '🥈' 
            : fasciaName.toLowerCase().includes('bronzo') ? '🥉' 
            : '🏅';

          return (
            <div key={fasciaName} className={`${colorClasses.bg} border-2 ${colorClasses.border} rounded-xl overflow-hidden`}>
              <div className={`${colorClasses.header} p-4 border-b ${colorClasses.border}`}>
                <h4 className={`font-bold ${colorClasses.text} flex items-center gap-2`}>
                  <span className="text-2xl">{icon}</span>
                  <span className="capitalize text-lg">{fasciaName}</span>
                  <span className="text-sm font-normal opacity-75">({fasciaQualified.length} giocatori)</span>
                </h4>
              </div>
              
              <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                {fasciaQualified.map((q, i) => (
                  <div 
                    key={q.participant_id}
                    className={`bg-white border ${colorClasses.border} rounded-xl p-4 text-center hover:shadow-md transition-shadow`}
                  >
                    <span className={`text-2xl font-bold ${colorClasses.text}`}>
                      {q.seed || i + 1}°
                    </span>
                    <p className="font-medium mt-2">{q.team_name || q.name}</p>
                    {q.team_name && <p className="text-xs text-gray-400">({q.name})</p>}
                    <p className="text-sm text-gray-500">Girone {q.source_group}</p>
                    <p className={`text-xs ${colorClasses.text} font-medium`}>{q.source_position}° nel girone</p>
                    <span className={`mt-2 inline-block text-xs px-2 py-0.5 rounded-full ${getMethodBadgeColor(q.qualification_method)}`}>
                      {getQualificationLabel(q.qualification_method)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  if (qualified.length > 0) {
    // Separa qualificati diretti da trasversali
    const directQualified = qualified.filter(q => q.qualification_method === 'direct');
    const transversalQualified = qualified.filter(q => q.qualification_method?.startsWith('transversal_'));

    return (
      <div className="space-y-8">
        <div className="text-center">
          <span className="text-6xl">🎯</span>
          <h3 className="text-2xl font-bold text-gray-800 mt-4">Qualificati alla Fase Eliminazione</h3>
          <p className="text-gray-500 mt-2">
            {qualified.length} giocatori qualificati • Metodo: {isTransversal ? 'Trasversale' : 'Diretto'}
          </p>
        </div>

        {/* Info metodo qualificazione */}
        {isTransversal && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <h4 className="font-bold text-blue-800 mb-2">📊 Metodo Trasversale</h4>
            <p className="text-blue-700 text-sm">
              I primi <span className="font-bold">{qualificationConfig.direct_positions || 1}</span> di ogni girone passano direttamente.
              {qualificationConfig.transversal_slots > 0 && (
                <> Più i <span className="font-bold">{qualificationConfig.transversal_slots}</span> migliori tra i secondi classificati 
                (ordinati per punti/partita, differenza reti, gol fatti).</>
              )}
            </p>
          </div>
        )}

        {/* Qualificati Diretti */}
        {directQualified.length > 0 && (
          <div>
            <h4 className="text-lg font-bold text-gray-700 mb-3 flex items-center gap-2">
              <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-sm">🏆 Qualificati Diretti</span>
              <span className="text-gray-400 font-normal">({directQualified.length})</span>
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {directQualified.map((q, i) => (
                <div 
                  key={q.participant_id}
                  className="bg-white border-2 border-green-200 rounded-xl p-4 text-center hover:shadow-md transition-shadow"
                >
                  <span className="text-2xl font-bold text-green-600">
                    {q.seed || i + 1}°
                  </span>
                  <p className="font-medium mt-2">{q.team_name || q.name}</p>
                  {q.team_name && <p className="text-xs text-gray-400">({q.name})</p>}
                  <p className="text-sm text-gray-500">Girone {q.source_group}</p>
                  <p className="text-xs text-green-600 font-medium">{q.source_position}° nel girone</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Qualificati Trasversali (Migliori Secondi/Terzi) */}
        {transversalQualified.length > 0 && (
          <div>
            <h4 className="text-lg font-bold text-gray-700 mb-3 flex items-center gap-2">
              <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-sm">📊 Migliori Secondi/Terzi</span>
              <span className="text-gray-400 font-normal">({transversalQualified.length})</span>
            </h4>
            <div className="bg-blue-50 rounded-xl p-4 mb-3">
              <p className="text-sm text-blue-700">
                Classifica basata su: <span className="font-medium">Punti per partita → Differenza reti → Gol segnati</span>
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {transversalQualified.map((q, i) => (
                <div 
                  key={q.participant_id}
                  className="bg-white border-2 border-blue-200 rounded-xl p-4 text-center hover:shadow-md transition-shadow"
                >
                  <span className="text-2xl font-bold text-blue-600">
                    {q.seed || directQualified.length + i + 1}°
                  </span>
                  <p className="font-medium mt-2">{q.team_name || q.name}</p>
                  {q.team_name && <p className="text-xs text-gray-400">({q.name})</p>}
                  <p className="text-sm text-gray-500">Girone {q.source_group}</p>
                  <div className="mt-2 flex flex-col gap-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${getMethodBadgeColor(q.qualification_method)}`}>
                      {getQualificationLabel(q.qualification_method)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Se tutto diretto (no separazione) */}
        {!isTransversal && directQualified.length === 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {qualified.map((q, i) => (
              <div 
                key={q.participant_id}
                className="bg-white border border-gray-200 rounded-xl p-4 text-center"
              >
                <span className={`text-3xl ${i < 4 ? 'text-green-500' : ''}`}>
                  {i + 1}°
                </span>
                <p className="font-medium mt-2">{q.team_name || q.name}</p>
                {q.team_name && <p className="text-xs text-gray-400">({q.name})</p>}
                <p className="text-sm text-gray-500">Girone {q.source_group}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Classifica Live dai gironi (per campionati e tornei in corso)
  const groups = tournament.groups || [];
  if (groups.length > 0) {
    return (
      <div className="space-y-6">
        <div className="text-center mb-6">
          <span className="text-5xl">🏅</span>
          <h3 className="text-2xl font-bold text-gray-800 mt-4">Classifica</h3>
          <p className="text-gray-500 mt-2">
            Classifica aggiornata in tempo reale
          </p>
        </div>

        {groups.map((group, groupIndex) => (
          <div key={group.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 border-b border-gray-200">
              <h4 className="font-bold text-gray-800">
                {groups.length === 1 ? '🏆 Classifica Generale' : `📋 Girone ${group.name}`}
              </h4>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-left">#</th>
                    <th className="px-3 py-3 text-left">Squadra/Giocatore</th>
                    <th className="px-2 py-3 text-center">G</th>
                    <th className="px-2 py-3 text-center">V</th>
                    <th className="px-2 py-3 text-center">N</th>
                    <th className="px-2 py-3 text-center">P</th>
                    <th className="px-2 py-3 text-center">GF</th>
                    <th className="px-2 py-3 text-center">GS</th>
                    <th className="px-2 py-3 text-center">DR</th>
                    <th className="px-2 py-3 text-center font-bold">Pt</th>
                  </tr>
                </thead>
                <tbody>
                  {(group.standings || []).map((entry, i) => (
                    <tr 
                      key={entry.participant_id} 
                      className={`
                        ${i === 0 ? 'bg-yellow-50' : ''}
                        ${i === 1 ? 'bg-gray-100' : ''}
                        ${i === 2 ? 'bg-orange-50' : ''}
                        hover:bg-blue-50 transition-colors
                      `}
                    >
                      <td className="px-3 py-3 font-bold">
                        {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : i + 1}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                          <ParticipantLogo 
                            logoUrl={entry.logo_url} 
                            name={entry.name} 
                            teamName={entry.team_name}
                            size="sm"
                          />
                          <div className="flex flex-col min-w-0">
                            <span className="font-bold text-gray-800 text-sm truncate">
                              {entry.team_name || entry.name}
                            </span>
                            {entry.team_name && (
                              <span className="text-xs text-gray-500 truncate">({entry.name})</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-3 text-center">{entry.matches_played || entry.played || 0}</td>
                      <td className="px-2 py-3 text-center text-green-600 font-medium">{entry.wins || entry.won || 0}</td>
                      <td className="px-2 py-3 text-center text-yellow-600">{entry.draws || entry.drawn || 0}</td>
                      <td className="px-2 py-3 text-center text-red-600">{entry.losses || entry.lost || 0}</td>
                      <td className="px-2 py-3 text-center">{entry.goals_for || 0}</td>
                      <td className="px-2 py-3 text-center">{entry.goals_against || 0}</td>
                      <td className="px-2 py-3 text-center">{entry.goal_difference || 0}</td>
                      <td className="px-2 py-3 text-center font-bold text-blue-600 text-lg">{entry.points || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="text-center py-12">
      <span className="text-6xl">📊</span>
      <p className="mt-4 text-gray-600">La classifica sarà disponibile dopo l'avvio del torneo.</p>
    </div>
  );
};


// ============================================
// ============================================
// IMPORTED MATCHES BY GIORNATA - Raggruppamento per giornata
// ============================================

const ImportedMatchesByGiornata = ({ partite, canAddResults, isConverted, isMatchLocked, setAddingResult }) => {
  const [selectedGiornata, setSelectedGiornata] = useState(null);

  // Check if matches have round_number for grouping
  const hasRoundNumbers = partite.some(p => p.round_number != null);

  // Group matches by round_number
  const matchesByGiornata = {};
  if (hasRoundNumbers) {
    partite.forEach(p => {
      const g = p.round_number || 0;
      if (!matchesByGiornata[g]) matchesByGiornata[g] = [];
      matchesByGiornata[g].push(p);
    });
  }
  const giornateList = Object.keys(matchesByGiornata).map(Number).sort((a, b) => a - b);

  // Auto-select first giornata
  const activeGiornata = selectedGiornata ?? (giornateList[0] || null);

  // Get status badge for a giornata
  const getGiornataBadge = (matches) => {
    const played = matches.filter(m => m.giocata).length;
    const total = matches.length;
    if (played === total) return { label: 'Completata', color: 'bg-green-100 text-green-700', icon: '✅' };
    if (played > 0) return { label: 'In corso', color: 'bg-yellow-100 text-yellow-700', icon: '⏳' };
    return { label: 'Da giocare', color: 'bg-gray-100 text-gray-500', icon: '📅' };
  };

  // Render a single match row
  const renderMatch = (partita, idx) => {
    const isPlayed = partita.giocata;
    return (
      <div
        key={partita.id || idx}
        className={`rounded-xl p-3 sm:p-4 flex items-center justify-between ${
          isPlayed ? 'bg-white shadow-sm' : 'bg-gray-50 border-2 border-dashed border-gray-200'
        }`}
        data-testid={`imported-match-${partita.id || idx}`}
      >
        <div className="flex-1 text-right min-w-0">
          <span className={`font-medium truncate block ${isPlayed ? 'text-gray-900' : 'text-gray-600'}`}>{partita.giocatore_casa}</span>
        </div>
        <div className="px-3 sm:px-6 text-center flex-shrink-0">
          {isPlayed ? (
            <div>
              <span className="text-xl font-bold">{partita.gol_casa} - {partita.gol_trasferta}</span>
              {partita.rigori_casa != null && (
                <span className="text-xs text-gray-400 block">rig. {partita.rigori_casa}-{partita.rigori_trasferta}</span>
              )}
              {isMatchLocked && isMatchLocked(partita.id) && <span className="text-xs text-gray-400">🔒</span>}
            </div>
          ) : canAddResults ? (
            <button
              onClick={() => setAddingResult(partita)}
              className="px-3 py-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 font-medium text-xs sm:text-sm"
              data-testid={`btn-add-result-imported-${idx}`}
            >
              Inserisci
            </button>
          ) : (
            <span className="text-gray-400 text-xs sm:text-sm">vs</span>
          )}
        </div>
        <div className="flex-1 text-left min-w-0">
          <span className={`font-medium truncate block ${isPlayed ? 'text-gray-900' : 'text-gray-600'}`}>{partita.giocatore_trasferta}</span>
        </div>
      </div>
    );
  };

  // FALLBACK: no round_number — flat list (backward compat)
  if (!hasRoundNumbers) {
    return (
      <div className="space-y-4">
        {canAddResults && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-center gap-2 text-blue-700">
              <span>ℹ️</span>
              <p className="text-sm">
                Torneo convertito: puoi aggiungere risultati alle partite non ancora giocate.
                I risultati già presenti sono <strong>bloccati</strong> (🔒).
              </p>
            </div>
          </div>
        )}
        <div>
          <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
            <span className="text-green-500">✓</span> Partite Giocate ({partite.filter(p => p.giocata).length})
          </h3>
          <div className="space-y-2">
            {partite.filter(p => p.giocata).map((p, i) => renderMatch(p, i))}
          </div>
        </div>
        {partite.filter(p => !p.giocata).length > 0 && (
          <div>
            <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
              <span className="text-yellow-500">⏳</span> Partite Da Giocare ({partite.filter(p => !p.giocata).length})
            </h3>
            <div className="space-y-2">
              {partite.filter(p => !p.giocata).map((p, i) => renderMatch(p, i))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // MAIN VIEW: grouped by giornata
  const currentMatches = matchesByGiornata[activeGiornata] || [];
  const currentBadge = currentMatches.length > 0 ? getGiornataBadge(currentMatches) : null;
  const totalPlayed = partite.filter(p => p.giocata).length;
  const totalMatches = partite.length;

  return (
    <div className="space-y-4" data-testid="imported-matches-by-giornata">
      {canAddResults && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-center gap-2 text-blue-700">
            <span>ℹ️</span>
            <p className="text-sm">
              Torneo convertito: puoi aggiungere risultati alle partite non ancora giocate.
              I risultati già presenti sono <strong>bloccati</strong> (🔒).
            </p>
          </div>
        </div>
      )}

      {/* Summary bar */}
      <div className="flex items-center justify-between bg-white rounded-xl p-3 shadow-sm">
        <span className="text-sm text-gray-500">{giornateList.length} giornate</span>
        <span className="text-sm font-medium text-gray-700">{totalPlayed}/{totalMatches} partite giocate</span>
      </div>

      {/* Quick navigation */}
      <div className="flex flex-wrap gap-1.5" data-testid="giornata-nav">
        {giornateList.map(g => {
          const gMatches = matchesByGiornata[g];
          const badge = getGiornataBadge(gMatches);
          const isActive = g === activeGiornata;
          return (
            <button
              key={g}
              onClick={() => setSelectedGiornata(g)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                isActive
                  ? 'bg-blue-600 text-white shadow-md'
                  : badge.icon === '✅'
                    ? 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'
                    : badge.icon === '⏳'
                      ? 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100 border border-yellow-200'
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
              }`}
              data-testid={`giornata-btn-${g}`}
            >
              G.{g}
            </button>
          );
        })}
      </div>

      {/* Current giornata content */}
      {activeGiornata != null && currentMatches.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid={`giornata-panel-${activeGiornata}`}>
          {/* Giornata header */}
          <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <span className="text-2xl font-bold text-blue-600">{activeGiornata}</span>
              <h3 className="font-bold text-gray-800">Giornata {activeGiornata}</h3>
            </div>
            <div className="flex items-center gap-2">
              {currentBadge && (
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${currentBadge.color}`} data-testid={`giornata-badge-${activeGiornata}`}>
                  {currentBadge.icon} {currentBadge.label}
                </span>
              )}
              <span className="text-xs text-gray-400">
                {currentMatches.filter(m => m.giocata).length}/{currentMatches.length}
              </span>
            </div>
          </div>

          {/* Matches */}
          <div className="divide-y divide-gray-100 p-2 sm:p-3 space-y-2">
            {currentMatches.map((partita, idx) => renderMatch(partita, idx))}
          </div>
        </div>
      )}
    </div>
  );
};


// GROUPED STANDINGS VIEW - Gironi separati con tab
// ============================================

const GroupedStandingsView = ({ groups, knockoutBracket }) => {
  const [activeGroup, setActiveGroup] = useState(0);
  
  if (!groups || groups.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        Nessun girone disponibile
      </div>
    );
  }
  
  // Determina l'assegnazione fasce in base alla posizione nei gironi
  // 1° di ogni girone → 1ª fascia, 2° di ogni girone → 2ª fascia, etc.
  const getFasciaForPosition = (positionInGroup) => {
    const fasciaNames = ['1ª Fascia', '2ª Fascia', '3ª Fascia', '4ª Fascia'];
    const bracketRounds = knockoutBracket?.rounds || [];
    
    // Se ci sono bracket rounds, usa quelli come nomi delle fasce
    if (bracketRounds.length > 0 && positionInGroup <= bracketRounds.length) {
      const round = bracketRounds[positionInGroup - 1];
      return round?.name || fasciaNames[positionInGroup - 1] || `${positionInGroup}ª Fascia`;
    }
    
    return fasciaNames[positionInGroup - 1] || `${positionInGroup}ª Fascia`;
  };
  
  const getFasciaColor = (positionInGroup) => {
    const colors = [
      'bg-yellow-100 text-yellow-800 border-yellow-400', // 1ª fascia - oro
      'bg-gray-200 text-gray-700 border-gray-400',       // 2ª fascia - argento
      'bg-amber-100 text-amber-800 border-amber-400',    // 3ª fascia - bronzo
      'bg-blue-100 text-blue-700 border-blue-400'        // 4ª fascia
    ];
    return colors[positionInGroup - 1] || 'bg-gray-100 text-gray-600 border-gray-300';
  };
  
  const currentGroup = groups[activeGroup];
  const standings = currentGroup?.standings || [];
  const groupMatches = currentGroup?.matches || [];
  
  return (
    <div className="space-y-4">
      {/* Tab gironi */}
      <div className="flex flex-wrap gap-2 bg-gray-50 p-3 rounded-xl">
        {groups.map((group, idx) => (
          <button
            key={idx}
            onClick={() => setActiveGroup(idx)}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeGroup === idx
                ? 'bg-blue-500 text-white shadow-md'
                : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
            }`}
            data-testid={`group-tab-${group.name}`}
          >
            {group.name || `Girone ${String.fromCharCode(65 + idx)}`}
          </button>
        ))}
      </div>
      
      {/* Classifica del girone selezionato */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4">
          <h3 className="font-bold text-lg">
            📋 {currentGroup?.name || `Girone ${String.fromCharCode(65 + activeGroup)}`}
          </h3>
          <p className="text-blue-100 text-sm">{standings.length} partecipanti</p>
        </div>
        
        <table className="w-full">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500">#</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500">Giocatore</th>
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-500">Fascia</th>
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-500">G</th>
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-500">V</th>
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-500">P</th>
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-500">S</th>
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-500">GF</th>
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-500">GS</th>
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-500">DR</th>
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 font-bold">Pt</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {standings.map((player, idx) => {
              const position = idx + 1;
              const fasciaName = getFasciaForPosition(position);
              const fasciaColor = getFasciaColor(position);
              const diffReti = (player.gol_fatti || 0) - (player.gol_subiti || 0);
              
              return (
                <tr 
                  key={player.nome || idx} 
                  className={idx === 0 ? 'bg-yellow-50/50' : ''}
                  data-testid={`group-standing-${idx}`}
                >
                  <td className="px-3 py-3 text-sm font-medium">
                    {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : position}
                  </td>
                  <td className="px-3 py-3 text-sm">
                    <div className="font-medium text-gray-900">{player.nome}</div>
                    {player.squadra && player.squadra !== player.nome && (
                      <div className="text-xs text-gray-500">{player.squadra}</div>
                    )}
                  </td>
                  <td className="px-3 py-3 text-center">
                    <span className={`text-xs px-2 py-1 rounded-full border ${fasciaColor}`}>
                      {fasciaName}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-sm text-center text-gray-600">{player.partite || 0}</td>
                  <td className="px-3 py-3 text-sm text-center text-green-600">{player.vittorie || 0}</td>
                  <td className="px-3 py-3 text-sm text-center text-yellow-600">{player.pareggi || 0}</td>
                  <td className="px-3 py-3 text-sm text-center text-red-600">{player.sconfitte || 0}</td>
                  <td className="px-3 py-3 text-sm text-center text-gray-600">{player.gol_fatti || 0}</td>
                  <td className="px-3 py-3 text-sm text-center text-gray-600">{player.gol_subiti || 0}</td>
                  <td className="px-3 py-3 text-sm text-center font-medium" style={{color: diffReti >= 0 ? '#16a34a' : '#dc2626'}}>
                    {diffReti > 0 ? '+' : ''}{diffReti}
                  </td>
                  <td className="px-3 py-3 text-sm text-center font-bold text-blue-600 text-lg">{player.punti || 0}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      {/* Partite del girone */}
      {groupMatches.length > 0 && (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <div className="bg-gray-100 p-3 border-b">
            <h4 className="font-medium text-gray-700">
              ⚽ Partite del girone ({groupMatches.length})
            </h4>
          </div>
          <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
            {groupMatches.map((match, idx) => {
              const homeGoals = match.gol_casa ?? match.score_a ?? '-';
              const awayGoals = match.gol_trasferta ?? match.score_b ?? '-';
              const home = match.giocatore_casa || match.team_a_name || '?';
              const away = match.giocatore_trasferta || match.team_b_name || '?';
              const played = match.giocata || match.played;
              
              return (
                <div key={idx} className="px-4 py-2 flex items-center justify-between text-sm">
                  <span className={`flex-1 text-right ${played && homeGoals > awayGoals ? 'font-bold text-green-600' : ''}`}>
                    {home}
                  </span>
                  <span className="mx-4 px-3 py-1 bg-gray-100 rounded font-mono font-bold">
                    {played ? `${homeGoals} - ${awayGoals}` : 'vs'}
                  </span>
                  <span className={`flex-1 text-left ${played && awayGoals > homeGoals ? 'font-bold text-green-600' : ''}`}>
                    {away}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};


// Component to view imported tournament details
export const ImportedTournamentDetail = ({ token, tournamentId, onBack, onRefresh }) => {
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('classifica');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showConvertConfirm, setShowConvertConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [converting, setConverting] = useState(false);
  const [addingResult, setAddingResult] = useState(null); // match being edited
  const [resultScore, setResultScore] = useState({ home: 0, away: 0 });

  useEffect(() => {
    fetchTournament();
  }, [tournamentId]);

  const fetchTournament = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/clubs/import-torneo-link/imported/${tournamentId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Errore caricamento torneo');
      
      const data = await response.json();
      setTournament(data.tournament);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTournament = async () => {
    try {
      setDeleting(true);
      const response = await fetch(`${BACKEND_URL}/api/clubs/import-torneo-link/imported/${tournamentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Errore durante eliminazione');
      }
      
      // Success - go back and refresh list
      if (onRefresh) onRefresh();
      onBack();
    } catch (err) {
      setError(err.message);
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  // Convert tournament to native
  const handleConvertToNative = async () => {
    try {
      setConverting(true);
      const response = await fetch(`${BACKEND_URL}/api/clubs/import-torneo-link/imported/${tournamentId}/convert`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Errore durante conversione');
      }
      
      // Refresh tournament data
      await fetchTournament();
      setShowConvertConfirm(false);
    } catch (err) {
      setError(err.message);
      setShowConvertConfirm(false);
    } finally {
      setConverting(false);
    }
  };

  // Add result to a pending match
  const handleAddResult = async (matchId) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/clubs/import-torneo-link/imported/${tournamentId}/add-result?match_id=${matchId}&score_home=${resultScore.home}&score_away=${resultScore.away}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Errore durante inserimento risultato');
      }
      
      const data = await response.json();
      
      // Refresh tournament data
      await fetchTournament();
      setAddingResult(null);
      setResultScore({ home: 0, away: 0 });
      
      // Check if all results added
      if (data.all_results_added) {
        alert('🎉 Tutti i risultati sono stati inseriti! Puoi chiudere il torneo.');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Check if match result is locked
  const isMatchLocked = (matchId) => {
    return tournament?.locked_match_ids?.includes(matchId) || false;
  };

  // Check if tournament is converted
  const isConverted = tournament?.converted_to_native || false;
  const canAddResults = isConverted && tournament?.can_add_results;
  const allResultsAdded = tournament?.all_results_added || false;
  const isCompleted = tournament?.status === 'completed';
  
  // Count pending matches
  const pendingMatches = tournament?.partite?.filter(p => !p.giocata)?.length || 0;
  const canFinalize = isConverted && pendingMatches === 0 && !isCompleted;

  // Finalize tournament (close championship)
  const [showFinalizeConfirm, setShowFinalizeConfirm] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [finalizeResult, setFinalizeResult] = useState(null);

  const handleFinalizeTournament = async () => {
    try {
      setFinalizing(true);
      const response = await fetch(`${BACKEND_URL}/api/clubs/import-torneo-link/imported/${tournamentId}/finalize`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Errore durante chiusura');
      }
      
      const data = await response.json();
      setFinalizeResult(data);
      setShowFinalizeConfirm(false);
      
      // Refresh tournament data
      await fetchTournament();
    } catch (err) {
      setError(err.message);
      setShowFinalizeConfirm(false);
    } finally {
      setFinalizing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 rounded-xl p-6 text-center">
        <span className="text-4xl">❌</span>
        <p className="mt-2 text-red-600">{error}</p>
        <button onClick={onBack} className="mt-4 px-4 py-2 bg-gray-200 rounded-lg">Torna indietro</button>
      </div>
    );
  }

  if (!tournament) return null;

  return (
    <div className="space-y-6">
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-[10000] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full z-[10001]">
            <div className="text-center">
              <span className="text-5xl">⚠️</span>
              <h3 className="mt-4 text-xl font-bold text-gray-900">Eliminare questo torneo?</h3>
              <p className="mt-2 text-gray-600">
                Stai per eliminare "<strong>{tournament.nome}</strong>".
                <br />Questa azione non può essere annullata.
              </p>
            </div>
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 disabled:opacity-50"
              >
                Annulla
              </button>
              <button
                onClick={handleDeleteTournament}
                disabled={deleting}
                className="flex-1 px-4 py-3 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600 disabled:opacity-50"
                data-testid="btn-confirm-delete-imported"
              >
                {deleting ? 'Eliminazione...' : '🗑️ Elimina'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Convert Confirmation Modal */}
      {showConvertConfirm && (
        <div className="fixed inset-0 bg-black/50 z-[10000] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full z-[10001]">
            <div className="text-center">
              <span className="text-5xl">🔄</span>
              <h3 className="mt-4 text-xl font-bold text-gray-900">Convertire in torneo nativo?</h3>
              <div className="mt-4 text-left bg-blue-50 rounded-xl p-4 text-sm text-gray-700">
                <p className="font-medium mb-2">Convertendo potrai aggiungere risultati mancanti nell'app.</p>
                <ul className="space-y-1 list-disc list-inside">
                  <li>I <strong>risultati già inseriti</strong> NON potranno essere modificati</li>
                  <li>Potrai aggiungere risultati alle <strong>partite aperte</strong></li>
                  <li>I <strong>giocatori</strong> non potranno essere modificati</li>
                  <li>Il <strong>formato</strong> non potrà essere modificato</li>
                </ul>
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setShowConvertConfirm(false)}
                disabled={converting}
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 disabled:opacity-50"
              >
                Annulla
              </button>
              <button
                onClick={handleConvertToNative}
                disabled={converting}
                className="flex-1 px-4 py-3 bg-blue-500 text-white rounded-xl font-medium hover:bg-blue-600 disabled:opacity-50"
                data-testid="btn-confirm-convert"
              >
                {converting ? 'Conversione...' : '🔄 Converti'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Result Modal */}
      {addingResult && (
        <div className="fixed inset-0 bg-black/50 z-[10000] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full z-[10001]">
            <div className="text-center">
              <span className="text-5xl">⚽</span>
              <h3 className="mt-4 text-xl font-bold text-gray-900">Inserisci Risultato</h3>
              <p className="mt-2 text-gray-600">
                {addingResult.giocatore_casa} vs {addingResult.giocatore_trasferta}
              </p>
            </div>
            <div className="mt-6 flex items-center justify-center gap-4">
              <div className="text-center">
                <p className="text-sm text-gray-500 mb-2">{addingResult.giocatore_casa}</p>
                <input
                  type="number"
                  min="0"
                  value={resultScore.home}
                  onChange={(e) => setResultScore(prev => ({ ...prev, home: parseInt(e.target.value) || 0 }))}
                  className="w-20 h-16 text-3xl text-center border-2 border-gray-300 rounded-xl focus:border-blue-500"
                />
              </div>
              <span className="text-3xl text-gray-400">-</span>
              <div className="text-center">
                <p className="text-sm text-gray-500 mb-2">{addingResult.giocatore_trasferta}</p>
                <input
                  type="number"
                  min="0"
                  value={resultScore.away}
                  onChange={(e) => setResultScore(prev => ({ ...prev, away: parseInt(e.target.value) || 0 }))}
                  className="w-20 h-16 text-3xl text-center border-2 border-gray-300 rounded-xl focus:border-blue-500"
                />
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => { setAddingResult(null); setResultScore({ home: 0, away: 0 }); }}
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200"
              >
                Annulla
              </button>
              <button
                onClick={() => handleAddResult(addingResult.id)}
                className="flex-1 px-4 py-3 bg-green-500 text-white rounded-xl font-medium hover:bg-green-600"
                data-testid="btn-confirm-add-result"
              >
                ✓ Salva Risultato
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Finalize Confirmation Modal */}
      {showFinalizeConfirm && (
        <div className="fixed inset-0 bg-black/50 z-[10000] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full z-[10001]">
            <div className="text-center">
              <span className="text-5xl">🏁</span>
              <h3 className="mt-4 text-xl font-bold text-gray-900">Chiudi Campionato</h3>
              <div className="mt-4 text-left bg-green-50 rounded-xl p-4 text-sm text-gray-700">
                <p className="font-medium mb-2">Stai per chiudere definitivamente questo campionato.</p>
                <ul className="space-y-1 list-disc list-inside">
                  <li>Il <strong>vincitore</strong> sarà il primo in classifica</li>
                  <li>Verrà aggiunto all'<strong>Albo d'Oro</strong> con podio 🥇🥈🥉</li>
                  <li>Il campionato <strong>NON sarà più modificabile</strong></li>
                </ul>
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setShowFinalizeConfirm(false)}
                disabled={finalizing}
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 disabled:opacity-50"
              >
                Annulla
              </button>
              <button
                onClick={handleFinalizeTournament}
                disabled={finalizing}
                className="flex-1 px-4 py-3 bg-green-500 text-white rounded-xl font-medium hover:bg-green-600 disabled:opacity-50"
                data-testid="btn-confirm-finalize"
              >
                {finalizing ? 'Chiusura...' : '🏁 Chiudi Campionato'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Finalize Success Modal */}
      {finalizeResult && (
        <div className="fixed inset-0 bg-black/50 z-[10000] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full z-[10001]">
            <div className="text-center">
              <span className="text-6xl">🎉</span>
              <h3 className="mt-4 text-2xl font-bold text-gray-900">Campionato Chiuso!</h3>
              <div className="mt-6 space-y-3">
                <div className="bg-yellow-50 rounded-xl p-4">
                  <span className="text-4xl">🥇</span>
                  <p className="text-xl font-bold text-yellow-800 mt-2">{finalizeResult.winner}</p>
                  <p className="text-sm text-yellow-600">Vincitore</p>
                </div>
                {finalizeResult.podio?.filter(p => p && p.posizione > 1).map((p, i) => (
                  <div key={i} className="bg-gray-50 rounded-xl p-3 flex items-center gap-3">
                    <span className="text-2xl">{p.medaglia}</span>
                    <span className="font-medium">{p.nome}</span>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-sm text-gray-500">
                Aggiunto all'Albo d'Oro ✓
              </p>
            </div>
            <button
              onClick={() => { setFinalizeResult(null); onBack(); }}
              className="mt-6 w-full px-4 py-3 bg-green-500 text-white rounded-xl font-medium hover:bg-green-600"
            >
              Torna alla lista
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-pink-500 rounded-2xl p-6 text-white">
        <div className="flex justify-between items-start">
          <button 
            onClick={onBack}
            className="text-white/80 hover:text-white flex items-center gap-2"
          >
            ← Torna alla lista
          </button>
          <div className="flex gap-2">
            {/* Convert button - only show if not converted and not completed */}
            {!isConverted && tournament.status !== 'completed' && (
              <button
                onClick={() => setShowConvertConfirm(true)}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                title="Converti in torneo nativo per aggiungere risultati"
                data-testid="btn-convert-to-native"
              >
                🔄 Converti in Nativo
              </button>
            )}
            {/* Finalize button - only show if converted and all matches played */}
            {canFinalize && (
              <button
                onClick={() => setShowFinalizeConfirm(true)}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                title="Chiudi campionato e aggiungi all'Albo d'Oro"
                data-testid="btn-finalize-tournament"
              >
                🏁 Chiudi Campionato
              </button>
            )}
            {/* Completed badge */}
            {isCompleted && (
              <span className="px-4 py-2 bg-gray-500 text-white rounded-lg cursor-not-allowed">
                🔒 Completato
              </span>
            )}
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="px-4 py-2 bg-white/20 text-white rounded-lg hover:bg-red-500 transition-colors"
              title="Elimina torneo"
              data-testid="btn-delete-imported-tournament"
            >
              🗑️ Elimina
            </button>
          </div>
        </div>
        <div className="flex items-center gap-3 mt-4">
          <span className="text-4xl">📥</span>
          <div>
            <h1 className="text-2xl font-bold">{tournament.nome}</h1>
            <div className="flex items-center gap-2">
              <p className="text-white/80">Torneo Importato</p>
              {isConverted && (
                <span className="px-2 py-0.5 bg-green-500 text-white text-xs rounded-full">
                  Convertito ✓
                </span>
              )}
            </div>
          </div>
        </div>
        {tournament.source_url && (
          <a 
            href={tournament.source_url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="mt-4 inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-lg hover:bg-white/30"
          >
            🔗 Vedi fonte originale
          </a>
        )}
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 rounded-xl p-4 text-center">
          <div className="text-3xl font-bold text-blue-600">{tournament.statistics.participants}</div>
          <div className="text-sm text-gray-600">Partecipanti</div>
        </div>
        <div className="bg-green-50 rounded-xl p-4 text-center">
          <div className="text-3xl font-bold text-green-600">{tournament.statistics.played_matches}</div>
          <div className="text-sm text-gray-600">Partite Giocate</div>
        </div>
        <div className="bg-yellow-50 rounded-xl p-4 text-center">
          <div className="text-3xl font-bold text-yellow-600">{tournament.statistics.pending_matches}</div>
          <div className="text-sm text-gray-600">Da Giocare</div>
        </div>
        <div className="bg-orange-50 rounded-xl p-4 text-center">
          <div className="text-3xl font-bold text-orange-600">{tournament.statistics.total_goals}</div>
          <div className="text-sm text-gray-600">Gol Totali</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b overflow-x-auto">
        <button
          onClick={() => setActiveTab('classifica')}
          className={`px-6 py-3 font-medium whitespace-nowrap ${activeTab === 'classifica' ? 'border-b-2 border-orange-500 text-orange-600' : 'text-gray-500'}`}
        >
          📊 Classifica
        </button>
        {/* Tab Tabellone - solo se c'è knockout_bracket */}
        {tournament.knockout_bracket?.rounds?.length > 0 && (
          <button
            onClick={() => setActiveTab('tabellone')}
            className={`px-6 py-3 font-medium whitespace-nowrap ${activeTab === 'tabellone' ? 'border-b-2 border-orange-500 text-orange-600' : 'text-gray-500'}`}
            data-testid="tab-tabellone"
          >
            🏆 Tabellone
          </button>
        )}
        {/* Tab Classifica Finale - solo se c'è classifica_finale_globale */}
        {tournament.classifica_finale_globale?.length > 0 && (
          <button
            onClick={() => setActiveTab('classifica_finale')}
            className={`px-6 py-3 font-medium whitespace-nowrap ${activeTab === 'classifica_finale' ? 'border-b-2 border-orange-500 text-orange-600' : 'text-gray-500'}`}
            data-testid="tab-classifica-finale"
          >
            🎖️ Classifica Finale
          </button>
        )}
        <button
          onClick={() => setActiveTab('partite')}
          className={`px-6 py-3 font-medium whitespace-nowrap ${activeTab === 'partite' ? 'border-b-2 border-orange-500 text-orange-600' : 'text-gray-500'}`}
        >
          ⚽ Partite
        </button>
        <button
          onClick={() => setActiveTab('partecipanti')}
          className={`px-6 py-3 font-medium whitespace-nowrap ${activeTab === 'partecipanti' ? 'border-b-2 border-orange-500 text-orange-600' : 'text-gray-500'}`}
        >
          👥 Partecipanti
        </button>
      </div>

      {/* Tab Content - Tabellone */}
      {activeTab === 'tabellone' && tournament.knockout_bracket && (
        <div className="bg-white rounded-xl shadow p-4">
          <ImportedTournamentBracket
            knockoutBracket={tournament.knockout_bracket}
            tournamentName={tournament.nome}
            onMatchClick={(match) => {
              // Allow adding result if converted and match not played
              if (canAddResults && !match.giocata && !isMatchLocked(match.id)) {
                setAddingResult(match);
                setResultScore({ 
                  home: match.gol_casa || 0, 
                  away: match.gol_trasferta || 0 
                });
              }
            }}
          />
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'classifica' && (
        <div className="space-y-4">
          {/* Se ci sono gironi, mostra tab separati */}
          {tournament.groups && tournament.groups.length > 0 ? (
            <GroupedStandingsView 
              groups={tournament.groups} 
              knockoutBracket={tournament.knockout_bracket}
            />
          ) : (
            /* Fallback: classifica unica */
            <div className="bg-white rounded-xl shadow overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">#</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Squadra</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">Pt</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">G</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">V</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">P</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">S</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">GF</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">GS</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">DR</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(tournament.classifica || []).map((player, idx) => (
                    <tr key={player.giocatore || player.nome} className={idx < 3 ? 'bg-yellow-50/50' : ''} data-testid={`classifica-row-${idx}`}>
                      <td className="px-4 py-3 text-sm font-medium">
                        {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : idx + 1}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="font-medium text-gray-900">{player.nome}</div>
                        {player.giocatore && player.giocatore !== player.nome && (
                          <div className="text-xs text-gray-500">({player.giocatore})</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-center font-bold text-blue-600">{player.punti}</td>
                      <td className="px-4 py-3 text-sm text-center text-gray-600">{player.partite}</td>
                      <td className="px-4 py-3 text-sm text-center text-green-600">{player.vittorie}</td>
                      <td className="px-4 py-3 text-sm text-center text-yellow-600">{player.pareggi}</td>
                      <td className="px-4 py-3 text-sm text-center text-red-600">{player.sconfitte}</td>
                      <td className="px-4 py-3 text-sm text-center text-gray-600">{player.gol_fatti}</td>
                      <td className="px-4 py-3 text-sm text-center text-gray-600">{player.gol_subiti}</td>
                      <td className="px-4 py-3 text-sm text-center font-medium" style={{color: (player.differenza_reti || 0) >= 0 ? '#16a34a' : '#dc2626'}}>
                        {(player.differenza_reti || 0) > 0 ? '+' : ''}{player.differenza_reti || 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Tab Content - Classifica Finale Globale */}
      {activeTab === 'classifica_finale' && tournament.classifica_finale_globale && (
        <div className="space-y-6" data-testid="classifica-finale-container">
          {/* Podio Top 3 */}
          {tournament.classifica_finale_globale.length >= 3 && (
            <div className="grid grid-cols-3 gap-4">
              {[1, 0, 2].map(idx => {
                const p = tournament.classifica_finale_globale[idx];
                if (!p) return null;
                const medals = ['🥇', '🥈', '🥉'];
                const bgColors = ['bg-gradient-to-b from-yellow-100 to-yellow-50 border-yellow-300', 'bg-gradient-to-b from-gray-100 to-gray-50 border-gray-300', 'bg-gradient-to-b from-amber-100 to-amber-50 border-amber-300'];
                const heights = ['h-36', 'h-28', 'h-28'];
                const order = [1, 0, 2]; // 2nd, 1st, 3rd visual order
                return (
                  <div key={idx} className={`${bgColors[idx]} border-2 rounded-xl p-4 text-center flex flex-col items-center justify-end ${heights[idx]} ${idx === 0 ? 'mt-0' : 'mt-8'}`} data-testid={`podium-${idx + 1}`}>
                    <div className="text-3xl mb-1">{medals[idx]}</div>
                    <div className="font-bold text-gray-900 text-sm truncate w-full">{p.nome}</div>
                    <div className="text-xs text-gray-500 truncate w-full">{p.fascia}</div>
                    <div className="text-xs font-semibold text-blue-600 mt-1">+{p.punti_ranking} pt</div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Tabella classifica completa */}
          <div className="bg-white rounded-xl shadow overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-5 py-3">
              <h3 className="text-white font-bold text-base">Classifica Finale Torneo</h3>
              <p className="text-indigo-200 text-xs">Ranking basato sui piazzamenti per fascia</p>
            </div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">#</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Giocatore</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Fascia</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">G</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">V</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">GF</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">GS</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">Pt Ranking</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {tournament.classifica_finale_globale.map((entry, idx) => {
                  const posFascia = entry.posizione_fascia;
                  const labelFascia = posFascia === 1 ? 'Vincitore' : posFascia === 2 ? 'Finalista' : posFascia ? `${posFascia}° posto` : '-';
                  const isTopThree = idx < 3;
                  const medals = ['🥇', '🥈', '🥉'];
                  return (
                    <tr key={entry.nome} className={isTopThree ? 'bg-yellow-50/40' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} data-testid={`classifica-finale-row-${idx}`}>
                      <td className="px-4 py-3 text-sm font-bold text-gray-700">
                        {isTopThree ? medals[idx] : entry.posizione_globale}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-gray-900 text-sm">{entry.nome}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700">
                          {entry.fascia}
                        </span>
                        <span className="text-xs text-gray-400 ml-1">{labelFascia}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-center text-gray-600">{entry.partite}</td>
                      <td className="px-4 py-3 text-sm text-center text-green-600 font-medium">{entry.vittorie}</td>
                      <td className="px-4 py-3 text-sm text-center text-gray-600">{entry.gol_fatti}</td>
                      <td className="px-4 py-3 text-sm text-center text-gray-600">{entry.gol_subiti}</td>
                      <td className="px-4 py-3 text-sm text-center">
                        <span className="font-bold text-blue-600">+{entry.punti_ranking}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Info box */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-700">
            <strong>Come funziona:</strong> La classifica finale si basa sui piazzamenti nelle fasi ad eliminazione. 
            Il vincitore della fascia principale è 1°, il finalista 2°, i semifinalisti 3°-4°. 
            Poi si prosegue con la fascia successiva (5°-8°) e così via. 
            I punti ranking vengono sommati alla classifica stagionale.
          </div>
        </div>
      )}

      {activeTab === 'partite' && (
        <ImportedMatchesByGiornata
          partite={tournament.partite}
          canAddResults={canAddResults}
          isConverted={isConverted}
          isMatchLocked={isMatchLocked}
          setAddingResult={setAddingResult}
        />
      )}

      {activeTab === 'partecipanti' && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {tournament.partecipanti.map((p, idx) => (
            <div key={idx} className="bg-white rounded-xl p-4 shadow-sm text-center">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="text-2xl">👤</span>
              </div>
              <div className="font-medium text-gray-900">{p.nome}</div>
              {p.squadra && <div className="text-xs text-gray-500">{p.squadra}</div>}
              <div className={`text-xs mt-1 ${p.socio_id ? 'text-green-600' : 'text-gray-400'}`}>
                {p.socio_id ? '✓ Socio abbinato' : '○ Non abbinato'}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


export default TorneiV3List;
